import React, { useEffect, useState } from 'react'
import MainLayout from '../layouts/MainLayout'
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getProducts, getFilterOptions } from '../redux/actions/product';
import ProductCard from '../components/ProductCard';
import { Spin, Breadcrumb, Pagination, Select, Button, Collapse, Input, Tag, Row, Col, Card, Checkbox } from 'antd';
import { Helmet } from 'react-helmet-async';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Option } = Select;
const { Panel } = Collapse;

const Products = () => {
    const { products, totalPages, currentPage, filterOptions } = useSelector((state: any) => state.products);
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    // Fiyat aralıkları tanımı
    const PRICE_RANGES = [
        { label: '₺1,500 - ₺2,500', min: 1500, max: 2500, value: '1500-2500' },
        { label: '₺2,500 - ₺4,000', min: 2500, max: 4000, value: '2500-4000' },
        { label: '₺4,000 - ₺6,000', min: 4000, max: 6000, value: '4000-6000' },
        { label: '₺6,000 - ₺10,000', min: 6000, max: 10000, value: '6000-10000' },
        { label: '₺10,000+', min: 10000, max: 999999, value: '10000+' }
    ];

    // Filter states
    const [filters, setFilters] = useState<{
        category: string[];
        brand: string[];
        tire_type: string[];
        season: string[];
        tire_size: string | null;
        load_index: number[];
        speed_index: string[];
        price_ranges: string[];
        search: string;
        page: number;
        limit: number;
    }>({
        category: [],
        brand: [],
        tire_type: [],
        season: [],
        tire_size: null,
        load_index: [],
        speed_index: [],
        price_ranges: [],
        search: '',
        page: 1,
        limit: 12
    });

    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [searchSuggestions, setSearchSuggestions] = useState<any[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);

    // URL parametrelerini oku ve filtreleri uygula
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const search = searchParams.get('search');
        const category = searchParams.getAll('category');
        const brand = searchParams.getAll('brand');
        const tire_type = searchParams.getAll('tire_type');
        const season = searchParams.getAll('season');
        const tire_size = searchParams.get('tire_size');
        const price_ranges = searchParams.getAll('price_ranges');

        console.log('🔍 URL Params Debug:', {
            search,
            category,
            brand,
            tire_type,
            season,
            tire_size,
            price_ranges,
            fullURL: location.search
        });

        if (search || category.length > 0 || brand.length > 0 || tire_type.length > 0 || season.length > 0 || tire_size || price_ranges.length > 0) {
            setFilters(prev => ({
                ...prev,
                search: search || '',
                category: category,
                brand: brand,
                tire_type: tire_type,
                season: season,
                tire_size: tire_size,
                price_ranges: price_ranges
            }));

            // API'ye filtrelerle birlikte istek gönder
            const apiParams: any = {
                page: 1,
                limit: 12
            };

            if (search) apiParams.search = search;
            if (category.length > 0) apiParams.category = category;
            if (brand.length > 0) apiParams.brand = brand;
            if (tire_type.length > 0) apiParams.tire_type = tire_type;
            if (season.length > 0) apiParams.season = season;
            if (tire_size) apiParams.tire_size = tire_size;
            if (price_ranges.length > 0) apiParams.price_ranges = price_ranges;

            console.log('📤 API Request Params:', apiParams);

            dispatch<any>(getProducts(apiParams));
        } else {
            // Parametre yoksa tüm ürünleri getir
            console.log('📦 No filters, fetching all products');
            dispatch<any>(getProducts({ page: 1, limit: 12 }));
        }
    }, [location.search, dispatch]);

    useEffect(() => {
        dispatch<any>(getFilterOptions());
    }, [dispatch]);

    // priceRange artık kullanılmıyor, filterOptions sadece filter seçeneklerini göstermek için kullanılıyor

    // Arama önerileri getir (debounced)
    const handleSearchChange = (value: string) => {
        setFilters(prev => ({ ...prev, search: value }));

        // Önceki timer'ı temizle
        if (searchDebounceTimer) {
            clearTimeout(searchDebounceTimer);
        }

        if (value.length < 2) {
            setSearchSuggestions([]);
            return;
        }

        // 300ms sonra arama yap
        const timer = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const response = await axios.get(`/api/products/search-suggestions?q=${encodeURIComponent(value)}`);
                const suggestions = response.data.suggestions.map((s: any) => ({
                    value: s.value,
                    label: s.label // Bu zaten string olarak geliyor backend'den
                }));
                setSearchSuggestions(suggestions);
            } catch (error) {
                console.error('Arama önerileri yüklenirken hata:', error);
                setSearchSuggestions([]);
            } finally {
                setSearchLoading(false);
            }
        }, 300);

        setSearchDebounceTimer(timer);
    };

    // Arama önerisi seçildiğinde
    const handleSearchSelect = (value: string, option: any) => {
        handleFilterChange('search', value);
    };

    const handleFilterChange = (key: string, value: any) => {
        const newFilters = { ...filters, [key]: value, page: 1 };
        setFilters(newFilters);

        // API için filtre parametrelerini hazırla
        const apiParams: any = {
            page: 1,
            limit: newFilters.limit
        };

        // URL parametrelerini hazırla
        const urlParams = new URLSearchParams();

        // Array filtreleri sadece dolu olanları ekle
        if (newFilters.category.length > 0) {
            apiParams.category = newFilters.category;
            newFilters.category.forEach(c => urlParams.append('category', c));
        }
        if (newFilters.brand.length > 0) {
            apiParams.brand = newFilters.brand;
            newFilters.brand.forEach(b => urlParams.append('brand', b));
        }
        if (newFilters.tire_type.length > 0) {
            apiParams.tire_type = newFilters.tire_type;
            newFilters.tire_type.forEach(t => urlParams.append('tire_type', t));
        }
        if (newFilters.season.length > 0) {
            apiParams.season = newFilters.season;
            newFilters.season.forEach(s => urlParams.append('season', s));
        }
        if (newFilters.load_index.length > 0) {
            apiParams.load_index = newFilters.load_index;
            newFilters.load_index.forEach(l => urlParams.append('load_index', String(l)));
        }
        if (newFilters.speed_index.length > 0) {
            apiParams.speed_index = newFilters.speed_index;
            newFilters.speed_index.forEach(s => urlParams.append('speed_index', s));
        }

        // Tek değer filtreleri
        if (newFilters.tire_size) {
            apiParams.tire_size = newFilters.tire_size;
            urlParams.append('tire_size', newFilters.tire_size);
        }
        if (newFilters.search) {
            apiParams.search = newFilters.search;
            urlParams.append('search', newFilters.search);
        }
        if (newFilters.price_ranges.length > 0) {
            apiParams.price_ranges = newFilters.price_ranges;
            newFilters.price_ranges.forEach(pr => urlParams.append('price_ranges', pr));
        }

        // URL'i güncelle
        navigate(`/lastik?${urlParams.toString()}`, { replace: true });

        dispatch<any>(getProducts(apiParams));
    };

    const handlePageChange = (page: number) => {
        const newFilters = { ...filters, page };
        setFilters(newFilters);
        dispatch<any>(getProducts(newFilters));
    };

    const clearAllFilters = () => {
        const clearedFilters = {
            category: [],
            brand: [],
            tire_type: [],
            season: [],
            tire_size: null,
            load_index: [],
            speed_index: [],
            price_ranges: [],
            search: '',
            page: 1,
            limit: 12
        };
        setFilters(clearedFilters);

        // URL'i temizle
        navigate('/lastik', { replace: true });

        dispatch<any>(getProducts(clearedFilters));
    };

    const getActiveFiltersCount = () => {
        let count = 0;
        if (filters.category.length > 0) count += filters.category.length;
        if (filters.brand.length > 0) count += filters.brand.length;
        if (filters.tire_type.length > 0) count += filters.tire_type.length;
        if (filters.season.length > 0) count += filters.season.length;
        if (filters.tire_size) count++;
        if (filters.load_index.length > 0) count += filters.load_index.length;
        if (filters.speed_index.length > 0) count += filters.speed_index.length;
        if (filters.search) count++;
        if (filters.price_ranges.length > 0) count += filters.price_ranges.length;
        return count;
    };

    const selectedCategoryName = filters.category.length === 1
        ? filters.category[0] // Artık direkt isim kullanıyoruz
        : filters.category.length > 1
            ? `${filters.category.length} Kategori`
            : null;

    const selectedBrandName = filters.brand.length === 1 ? filters.brand[0] : null;
    const selectedSeasonName = filters.season.length === 1 ? filters.season[0] : null;
    const selectedTireSize = filters.tire_size;

    // SEO için dinamik içerik oluştur
    const generateSEOContent = () => {
        const parts: string[] = [];
        let title = '';
        let description = '';
        let h1Title = '';

        // Kategori bazlı SEO içeriği
        if (selectedCategoryName && !selectedCategoryName.includes('Kategori')) {
            const categoryName = selectedCategoryName.replace(' Lastikleri', '');

            if (selectedBrandName && selectedSeasonName) {
                // Marka + Kategori + Mevsim
                parts.push(selectedBrandName, categoryName, selectedSeasonName);
                title = `${selectedBrandName} ${categoryName} ${selectedSeasonName} Lastik Fiyatları | Sakarya Lastik`;
                description = `${selectedBrandName} ${categoryName} ${selectedSeasonName} lastik modelleri ve fiyatları. %100 Orijinal ürün garantisi. Ücretsiz kargo ve montaj. ${products?.length || 0} ürün bulundu.`;
                h1Title = `${selectedBrandName} ${categoryName} ${selectedSeasonName} Lastikleri`;
            } else if (selectedBrandName) {
                // Marka + Kategori
                parts.push(selectedBrandName, categoryName);
                title = `${selectedBrandName} ${categoryName} Lastik Fiyatları ve Modelleri | Sakarya Lastik`;
                description = `${selectedBrandName} ${categoryName} lastik çeşitleri uygun fiyatlarla. Orijinal ürün, ücretsiz kargo, profesyonel montaj hizmeti. ${products?.length || 0} model mevcut.`;
                h1Title = `${selectedBrandName} ${categoryName} Lastikleri`;
            } else if (selectedSeasonName) {
                // Kategori + Mevsim
                parts.push(categoryName, selectedSeasonName);
                title = `${categoryName} ${selectedSeasonName} Lastik Fiyatları | Sakarya Lastik`;
                description = `${categoryName} ${selectedSeasonName} lastik modelleri en uygun fiyatlarla. Tüm markalar, ücretsiz kargo, hızlı teslimat. ${products?.length || 0} farklı model.`;
                h1Title = `${categoryName} ${selectedSeasonName} Lastikleri`;
            } else {
                // Sadece Kategori
                parts.push(categoryName);
                title = `${categoryName} Lastik Fiyatları ve Modelleri | Sakarya Lastik`;
                description = `${categoryName} lastik çeşitleri ve fiyatları. Michelin, Bridgestone, Petlas ve daha fazlası. Ücretsiz kargo, profesyonel montaj hizmeti. ${products?.length || 0} ürün.`;
                h1Title = `${categoryName} Lastikleri`;
            }
        }
        // Marka bazlı SEO içeriği (kategori yoksa)
        else if (selectedBrandName) {
            if (selectedSeasonName) {
                parts.push(selectedBrandName, selectedSeasonName);
                title = `${selectedBrandName} ${selectedSeasonName} Lastik Fiyatları | Sakarya Lastik`;
                description = `${selectedBrandName} ${selectedSeasonName} lastik modelleri ve fiyatları. Orijinal ürünler, ücretsiz kargo, hızlı teslimat. ${products?.length || 0} model bulundu.`;
                h1Title = `${selectedBrandName} ${selectedSeasonName} Lastikleri`;
            } else {
                parts.push(selectedBrandName);
                title = `${selectedBrandName} Lastik Fiyatları ve Modelleri | Sakarya Lastik`;
                description = `${selectedBrandName} lastik çeşitleri uygun fiyatlarla. %100 Orijinal, ücretsiz kargo, profesyonel montaj. ${products?.length || 0} farklı model.`;
                h1Title = `${selectedBrandName} Lastikleri`;
            }
        }
        // Mevsim bazlı SEO içeriği (kategori ve marka yoksa)
        else if (selectedSeasonName) {
            parts.push(selectedSeasonName);
            title = `${selectedSeasonName} Lastik Fiyatları ve Modelleri | Sakarya Lastik`;
            description = `${selectedSeasonName} lastik çeşitleri en uygun fiyatlarla. Tüm markalar ve ebatlar. Ücretsiz kargo, hızlı teslimat. ${products?.length || 0} ürün mevcut.`;
            h1Title = `${selectedSeasonName} Lastikleri`;
        }
        // Ebat bazlı SEO
        else if (selectedTireSize) {
            parts.push(selectedTireSize);
            title = `${selectedTireSize} Lastik Fiyatları - Tüm Markalar | Sakarya Lastik`;
            description = `${selectedTireSize} ebat lastik modelleri ve fiyatları. Michelin, Bridgestone, Goodyear ve tüm markalar. ${products?.length || 0} model bulundu.`;
            h1Title = `${selectedTireSize} Lastikler`;
        }
        // Arama bazlı SEO
        else if (filters.search) {
            parts.push(filters.search);
            title = `${filters.search} - Lastik Arama Sonuçları | Sakarya Lastik`;
            description = `"${filters.search}" araması için ${products?.length || 0} sonuç bulundu. En uygun fiyatlar, ücretsiz kargo, hızlı teslimat.`;
            h1Title = `${filters.search} Arama Sonuçları`;
        }
        // Default SEO
        else {
            title = 'Lastik Fiyatları ve Modelleri - Tüm Markalar | Sakarya Lastik';
            description = 'Otomobil, kamyon, kamyonet, SUV lastikleri ve daha fazlası. En uygun fiyatlarla kaliteli lastik çeşitleri. Ücretsiz kargo ve profesyonel montaj hizmeti. Michelin, Bridgestone, Petlas, Goodyear ve tüm markalar.';
            h1Title = 'Tüm Lastikler';
        }

        // Keywords oluştur
        const keywords = [
            'lastik',
            'lastik fiyatları',
            'online lastik satışı',
            selectedBrandName && `${selectedBrandName} lastik`,
            selectedBrandName && `${selectedBrandName} lastik fiyatları`,
            selectedCategoryName && selectedCategoryName.replace(' Lastikleri', '').toLowerCase() + ' lastiği',
            selectedSeasonName && `${selectedSeasonName.toLowerCase()} lastiği`,
            selectedSeasonName && `${selectedSeasonName.toLowerCase()} lastik fiyatları`,
            selectedTireSize && `${selectedTireSize} lastik`,
            selectedTireSize && `${selectedTireSize} lastik fiyatı`,
            'lastik montaj',
            'uygun lastik',
            'kaliteli lastik',
            'ücretsiz kargo',
            'orijinal lastik'
        ].filter(Boolean).join(', ');

        return { title, description, keywords, h1Title };
    };

    // SEO İçerik Veritabanı - Kategori, Marka, Mevsim kombinasyonları
    const seoContentDatabase: any = {
        // Kategori bazlı içerikler
        categories: {
            'Otomobil': {
                title: 'Otomobil Lastikleri',
                content: `Otomobil lastikleri, binek araçlar için özel olarak tasarlanmış ve günlük kullanımda maksimum performans sunmak üzere geliştirilmiş lastik türleridir. Şehir içi trafiğinden otobanlara, kısa mesafeli yolculuklardan uzun seyahatlere kadar her türlü kullanım senaryosunda güvenli ve konforlu bir sürüş deneyimi sağlamak için optimize edilmişlerdir.

Modern otomobil lastikleri, ileri teknoloji ve yüksek kaliteli malzemelerle üretilir. Lastik sırt deseninden yan duvar yapısına, kullanılan kauçuk karışımından iç yapı teknolojisine kadar her detay, sürüş güvenliği, konfor, yakıt verimliliği ve uzun ömür hedefleriyle tasarlanmıştır. Günümüz lastikleri sadece aracı yolda tutmakla kalmaz, aynı zamanda fren mesafesini kısaltır, virajlarda stabilite sağlar ve yolcu konforunu maksimize eder.

**Otomobil Lastiği Seçiminde Dikkat Edilmesi Gerekenler:**

**1. Doğru Ebat Seçimi:** Aracınızın kullanım kılavuzunda belirtilen lastik ebatı, üretici tarafından o araç için optimize edilmiş ölçüdür. Kapı kenarında, yakıt deposu kapağında veya kullanım kılavuzunda bulunan lastik bilgi etiketinde genellikle iki veya üç farklı ebat önerisi bulunur. Örneğin 205/55 R16 91V gibi bir kodlama, lastiğin genişliği (205mm), yan duvar yüksekliği (55%), jant çapı (16 inç), yük indeksi (91) ve hız sembolünü (V) gösterir. Yanlış ebat seçimi, aracın performansını olumsuz etkiler, hız göstergesinde hata oluşturur ve güvenliği tehlikeye atar.

**2. Sürüş Alışkanlıkları:** Çoğunlukla şehir içinde mi yoksa otobanda mı kullanıyorsunuz? Günlük ortalama ne kadar km yapıyorsunuz? Agresif mi yoksa sakin bir sürüş tarzınız var? Bu sorulara verdiğiniz cevaplar, lastik seçiminizi doğrudan etkiler. Şehir içi kullanımda düşük yuvarlanma direncine sahip, yakıt tasarrufu sağlayan lastikler tercih edilirken, otoyol kullanımında yüksek hız stabilitesi ve düşük gürültü seviyesi önceliklidir. Spor sürüş için ise yüksek grip ve viraj performansı sunan lastikler idealdir.

**3. Mevsimsel Koşullar:** Türkiye'nin iklim koşulları bölgeden bölgeye büyük farklılık gösterir. Kışları sert geçen bölgelerde yaşıyorsanız, 7°C altında yumuşak kalabilen özel kauçuk karışımına sahip kış lastikleri hayati önem taşır. Ilıman iklim bölgelerinde ise 4 mevsim lastikler pratik bir çözüm sunar. Yaz lastikleri ise yüksek sıcaklıklarda optimal performans gösterir ve kuru/ıslak zeminde mükemmel yol tutuş sağlar. 

**4. Yakıt Tüketimi ve Çevresel Etki:** Modern lastikler, AB lastik etiketleme sistemi ile yakıt verimliliği, ıslak zemin tutuşu ve gürültü seviyesi açısından sınıflandırılır. A'dan G'ye kadar olan enerji verimliliği sınıfı, lastiğin yuvarlanma direncini gösterir. Düşük yuvarlanma direnci, motora daha az yük bindirerek yakıt tüketimini %7-8 oranında azaltabilir. Bu hem cüzdanınızı hem de çevreyi korur. Yıllık 20.000 km yapan bir araç için bu tasarruf, 200-300 litre yakıta denk gelebilir.

**5. Gürültü Seviyesi:** Kabin içi gürültü, uzun yolculuklarda yorgunluğu artıran önemli bir faktördür. Lastik gürültüsü, sırt deseninin hava ile etkileşiminden kaynaklanır. Modern lastikler, özel desenleme teknikleri ve gürültü azaltıcı iç katmanlar sayesinde sessiz bir sürüş deneyimi sunar. AB etiketinde desibel seviyesi belirtilir ve bazı premium lastikler, acoustic foam teknolojisi ile kabin içi gürültüyü %9'a kadar azaltabilir.

**6. Lastik Yorumları ve Test Sonuçları:** ADAC, TÜV, Auto Bild, Auto Motor und Sport gibi bağımsız kuruluşlar, düzenli olarak lastik testleri yapar. Bu testler, fren mesafesi, kuru/ıslak tutuş, aquaplaning direnci, viraj performansı, konfor, gürültü ve aşınma direnci gibi kriterleri ölçer. Lastik seçiminizde bu test sonuçlarını dikkate almak, objektif bir karar vermenizi sağlar.

**Otomobil Lastik Fiyatları ve Segmentler:**

**Ekonomik Segment (₺800-1500):** Petlas, Starmaxx, Lassa gibi yerli markalar bu segmentte güçlü seçenekler sunar. Günlük ortalama 20-30 km yapan, şehir içi kullanım ağırlıklı sürücüler için ideal olan bu lastikler, kabul edilebilir performans ve uzun garanti süreleriyle öne çıkar. Türk mühendisler tarafından Avrupa standartlarında üretilen bu lastikler, ekonomik fiyat avantajı sağlarken temel güvenlik ve konfor gereksinimlerini karşılar.

**Orta Segment (₺1500-2500):** Hankook, Kumho, Nexen, Toyo gibi Asya markaları ile Fulda, Sava gibi Avrupa'nın ekonomik markaları bu kategoride yer alır. Fiyat/performans dengesi arayan, yıllık 15.000-25.000 km yapan sürücüler için uygun olan bu lastikler, modern teknolojiler içerir ve çoğu uluslararası otomobil üreticisi tarafından orijinal donanım (OE) olarak kullanılır.

**Premium Segment (₺2500-4500):** Michelin, Bridgestone, Continental, Pirelli, Goodyear dünya çapında tanınan premium markalardır. Araç performansını maksimize etmek isteyen, güvenlik ve konforda ödün vermeyen, yüksek km yapan sürücüler için tasarlanan bu lastikler, en son teknolojileri içerir. Uzun ömürlü tasarımları sayesinde km başına maliyet, orta segment lastiklerle rekabet edebilir seviyededir. Formula 1, WRC gibi yarışlarda geliştirilen teknolojilerin sivil kullanıma aktarıldığı bu lastikler, her koşulda üstün performans garantisi verir.

**Ultra Premium / Performans Segment (₺4500+):** Michelin Pilot Sport serisi, Pirelli P Zero, Continental SportContact, Bridgestone Potenza gibi ultra performans lastikleri, spor otomobiller ve yüksek beygir gücüne sahip araçlar için tasarlanmıştır. 240+ km/h hız kapasitesi, mükemmel viraj performansı ve minimum fren mesafesi sunan bu lastikler, performans tutkunları için ideal seçimdir.

**Lastik Bakımı ve Ömrü:** Doğru basınç kontrolü (ayda bir kez), düzenli balans ve rot ayarı (her 10.000 km'de), yük dağılımına dikkat, agresif sürüşten kaçınma gibi basit önlemlerle lastik ömrü 50.000-80.000 km'ye çıkarılabilir. Modern lastiklerin çoğu, aşınma göstergelerine (TWI - Tread Wear Indicator) sahiptir ve sırt derinliği 1.6 mm'nin altına düştüğünde değiştirilmelidir. Ancak güvenlik için 3-4 mm derinlikte değiştirmek önerilir.

Sakarya Lastik olarak, tüm segmentlerde geniş ürün yelpazemiz, uzman kadromuzun ücretsiz danışmanlığı, profesyonel montaj hizmetimiz ve garanti güvencemizle hizmetinizdeyiz.`,
                keywords: ['otomobil lastiği', 'araba lastiği', 'binek lastik', 'otomobil lastik fiyatları', 'araç lastiği', 'oto lastik']
            },
            'SUV': {
                title: 'SUV Lastikleri',
                content: `SUV (Sport Utility Vehicle) lastikleri, yüksek hacimli ve ağır araçlar için özel olarak mühendislik yapılmış, hem şehir içi konforunu hem de arazi performansını bir arada sunan özel lastik kategorisidir. Crossover'lardan tam donanımlı off-road araçlara kadar geniş bir yelpazede kullanılan bu lastikler, otomobil lastiklerine göre %20-40 daha fazla yük taşıma kapasitesine sahiptir ve daha zorlu kullanım koşullarına dayanacak şekilde tasarlanmıştır.

Modern SUV'ların artan popülaritesi ile birlikte, lastik üreticileri bu segment için özel teknolojiler geliştirmiştir. Yüksek tork, ağır kasa yapısı ve değişken zemin koşulları göz önünde bulundurularak tasarlanan SUV lastikleri, otomobil lastiklerinden farklı özellikler taşır. Hem asfalt yollarda konforlu sürüş hem de hafif arazi koşullarında yeterli çekiş sağlayacak şekilde optimize edilmişlerdir.

**SUV Lastiği Teknik Özellikleri ve Avantajları:**

**1. Yüksek Yük Taşıma Kapasitesi:** SUV lastikleri, yük indeksi (Load Index) açısından otomobil lastiklerinden belirgin şekilde ayrılır. Ortalama bir otomobil lastiğinin yük indeksi 80-95 arasında iken, SUV lastikleri 100-120 aralığında değerler gösterir. Bu, her bir lastiğin 800-1450 kg arası yük taşıyabildiği anlamına gelir. Örneğin, 108 yük indeksli bir lastik, lastik başına 1000 kg taşıma kapasitesine sahiptir. Bu özellik, ağır SUV kasası, yolcular, bagaj ve römork çekme gibi ekstra yükler için kritik öneme sahiptir.

**2. Güçlendirilmiş Yan Duvar Yapısı:** SUV lastiklerinin yan duvarları, çok katlı naylon ve polyester şeritlerle güçlendirilmiştir. Bu takviye, birkaç önemli avantaj sağlar: Yüksek ağırlığa dayanıklılık, bordür çarpmaları ve yan darbelere karşı koruma, arazi kullanımında taş ve kaya çarpmalarına direnç, yüksek hız stabilitesi. XL (Extra Load) veya Reinforced işaretli SUV lastikleri, standart lastiklere göre %20 daha fazla basınç taşıyabilir ve daha dayanıklıdır.

**3. Arazi ve Asfalt Çift Performans:** SUV lastikleri, genellikle üç kategoride değerlendirilir: Highway Terrain (H/T) - %80 asfalt + %20 hafif arazi için optimize edilmiş, şehir kullanımı ağırlıklı SUV'lar için ideal. All-Terrain (A/T) - %60 asfalt + %40 arazi dengesi, hafta sonu macera seven ama günlük işe gidip gelen sürücüler için. Mud-Terrain (M/T) - %40 asfalt + %60 ağır arazi, çamur, kum ve kayalık zeminlerde maksimum çekiş. Highway lastikler konfor ve sessizlik sunarken, All-Terrain modeller çok yönlülük, Mud-Terrain lastikler ise extreme çekiş gücü sağlar.

**4. Gelişmiş Sırt Deseni ve Su Tahliyesi:** SUV lastiklerinin daha derin sırt desenleri vardır (genellikle 9-12 mm, arazi lastiklerinde 15-18 mm). Bu derin kanallar, hem yağmurlu havalarda suyu daha etkin tahliye ederek aquaplaning riskini azaltır, hem de toprak, çamur gibi yumuşak zeminlerde self-cleaning (kendini temizleme) özelliği gösterir. Geniş çevre kanalları yağmurda güvenlik sağlarken, yan bloklardaki siping (ince kesikler) kar ve buz tutuşunu artırır.

**5. Uzun Ömürlü ve Dayanıklı Yapı:** SUV lastiklerinde kullanılan kauçuk karışımı, aşınmaya karşı daha dirençlidir. Premium SUV lastikleri, 60.000-80.000 km ömür sunabilir. Bazı üreticiler, 100.000 km garantili modeller bile sunar. Simetrik veya asimetrik sırt desenleri, düzenli rotasyon yapıldığında eşit aşınma sağlar. Steel-belted (çelik şeritli) radyal yapı, yüksek dayanıklılık ve stabilite garantisi verir.

**6. Yüksek Tork ve Fren Yükü Yönetimi:** SUV'ların güçlü motorları ve yüksek torku, lastiklere büyük yük bindirir. Modern SUV lastikleri, bu yüksek torku zemine aktaracak ve hızlı frenleme sırasında oluşan ısıyı yönetecek şekilde tasarlanmıştır. Özel kauçuk karışımları, yüksek sıcaklıklarda bile lastik performansını korur ve fren mesafesini minimize eder.

**7. Gürültü Kontrolü ve Konfor:** Eski nesil SUV lastikleri gürültülü olmasıyla bilinirken, modern lastikler sessiz sürüş teknolojileri içerir. Değişken sırt deseni (variable pitch pattern), rezonansı kırarak gürültüyü azaltır. Foam-filled (köpük dolgulu) lastikler, kabin içi gürültüyü %9'a kadar düşürebilir. Esnek yan duvar tasarımı, yol düzensizliklerini absorbe ederek konfor sağlar.

**Popüler SUV Lastik Serileri ve Özellikleri:**

**Continental CrossContact Serisi:** Alman mühendislik ürünü bu seri, LX25 (konfor), UHP (ultra yüksek performans), ATR (all-terrain) gibi farklı modeller sunar. ContiSilent teknolojisi ile sessiz sürüş, EcoContact+ teknolojisi ile yakıt tasarrufu sağlar. Özellikle Audi Q serileri, Mercedes GLE/GLC gibi premium SUV'larda orijinal donanım olarak kullanılır.

**Michelin Latitude Serisi:** Sport 3, Tour HP, Cross gibi varyantlarıyla geniş kullanım yelpazesi sunar. MaxTouch Construction teknolojisi ile uzun ömür, Green X ile düşük yakıt tüketimi sağlar. Latitude Cross, hafif arazi kullanımı için mud+snow sertifikasına sahiptir.

**Bridgestone Dueler Serisi:** H/P Sport (highway performance), A/T 001 (all-terrain), H/L 850 (highway luxury) modelleriyle her ihtiyaca cevap verir. NanoPro-Tech teknolojisi ile grip performansı artırılmış, Dueler H/L Alenza Plus ile %10 daha uzun ömür sunulur.

**Pirelli Scorpion Serisi:** Verde (çevreci), STR (street), ATR (all-terrain), MX (extreme off-road) varyantları mevcuttur. Seal Inside teknolojisi ile 4mm'ye kadar deliklerde iç tamir, Noise Cancelling System ile %25 daha sessiz kabin.

**Goodyear Wrangler Serisi:** AT Adventure (all-terrain), DuraTrac (ağır arazi), HP All Weather (4 mevsim) gibi geniş yelpaze. DualTread compound teknolojisi ile dış bloklar dayanıklı, iç bloklar konforlu yapıda tasarlanmış.

**SUV Lastik Fiyatları ve Segmentler:**

**Ekonomik Segment (₺1200-2000):** Petlas Explero, Lassa Competus, Starmaxx Incurro gibi yerli markalar bu segmentte güçlüdür. Günlük şehir kullanımı ağırlıklı, hafta sonları hafif arazi yapan sürücüler için ideal. Türk mühendislerin Avrupa standartlarında ürettiği bu lastikler, uygun fiyat avantajı sunar.

**Orta Segment (₺2000-3500):** Hankook Dynapro, Kumho Crugen, Yokohama Geolandar serileri bu kategoride. OEM kalitesinde lastikler, pek çok uluslararası otomobil markası tarafından fabrika çıkışlı kullanılır. Dengeli performans ve makul fiyat arayanlar için uygun.

**Premium Segment (₺3500-6000):** Michelin, Continental, Bridgestone, Pirelli, Goodyear dünya standartlarında SUV lastikleri sunar. Son teknoloji, maksimum güvenlik, uzun ömür arayan ve aracına değer veren sürücüler için. Km başına maliyet hesaplandığında, orta segment ile rekabet edebilir.

**Performance / Off-Road Segment (₺6000+):** BF Goodrich All-Terrain T/A, Goodyear Wrangler MT/R, Toyo Open Country M/T gibi extreme kullanım lastikleri. Profesyonel off-road, yarış kullanımı veya ağır arazi koşulları için tasarlanmış.

**SUV Lastik Seçim Rehberi:**

Compact SUV'lar (Dacia Duster, Nissan Qashqai, Renault Captur) için: 215/65 R16 - 215/60 R17 gibi ebatlar, H/T lastikler ideal. Orta boy SUV'lar (Toyota RAV4, Volkswagen Tiguan, Ford Kuga) için: 225/60 R17 - 235/55 R18 ebatlar, A/T veya H/T seçenekleri. Büyük SUV'lar (Toyota Land Cruiser, Range Rover, BMW X5) için: 255/50 R19 - 275/45 R20 ebatlar, premium H/T veya A/T lastikler. Off-Road SUV'lar (Jeep Wrangler, Land Rover Defender) için: 255/70 R16 - 265/75 R16 ebatlar, A/T veya M/T lastikler önerilir.

Sakarya Lastik olarak, tüm SUV modellerine uygun geniş lastik yelpazemiz, uzman montaj ekibimiz ve SUV'lar için özel ekipmanlarımızla (yüksek kaldırma kapasiteli lift, ağır lastik sökme takma makineleri) hizmetinizdeyiz.`,
                keywords: ['suv lastiği', 'suv lastik fiyatları', '4x4 lastik', 'crossover lastik', 'arazi lastiği', 'suv lastik']
            },
            'Kamyonet': {
                title: 'Kamyonet Lastikleri',
                content: `Kamyonet lastikleri, hafif ticari araçlar (LCV - Light Commercial Vehicle) için özel olarak tasarlanmış, yüksek yük taşıma kapasitesi, dayanıklılık ve düşük işletme maliyeti öncelikli lastiklerdir. Ford Transit, Mercedes Sprinter, Fiat Ducato, Volkswagen Transporter, Renault Master gibi ticari araçlar için optimize edilmiş bu lastikler, "C" (Commercial), "CP" (Commercial P-metric) veya "RF" (Reinforced) işaretleriyle tanınır ve binek araç lastiklerinden teknik özellikleri ile belirgin şekilde ayrılır.

Ticari kullanım koşulları, lastiklere çok daha büyük yük bindirir. Günde 200-400 km yol yapabilen, sürekli yük taşıyan, farklı yol koşullarında çalışan bu araçlar için lastik seçimi, işletme maliyetlerini doğrudan etkileyen kritik bir karardır. Modern kamyonet lastikleri, hem şehir içi dağıtım hem de şehirlerarası nakliye için optimize edilmiş özellikler sunar ve doğru seçildiğinde %15-25 oranında yakıt tasarrufu sağlayabilir.

**Kamyonet Lastiği Teknik Özellikleri:**

**1. Yüksek Yük İndeksi ve Basınç Değerleri:** Kamyonet lastiklerinin en belirgin özelliği, yüksek yük taşıma kapasitesidir. Standart bir binek araç lastiğinin yük indeksi 85-95 (515-690 kg/lastik) iken, kamyonet lastikleri 106-121 aralığında değerler gösterir (950-1450 kg/lastik). "C" işaretli lastikler, genellikle çift yük indeksi taşır (örneğin 215/65 R16 C 109/107T). İlk rakam tek montaj (109 = 1030 kg), ikinci rakam çift montaj (107 = 975 kg) için kapasiteyi gösterir. Bu lastikler, 5.5-7.0 bar arasında yüksek basınçta çalışır (binek araçlar 2.0-2.5 bar).

**2. Güçlendirilmiş Karkas Yapısı:** Kamyonet lastikleri, 8-10 katlı (ply rating) güçlendirilmiş karkasa sahiptir (binek lastikler genellikle 4 katlı). Çelik kordon şeritler, yüksek yük ve basınç altında lastiğin şeklini korur ve yan duvar patlamasını önler. Extra sidewall protection (ESP) teknolojisi, yükleme-boşaltma sırasında oluşan bordür çarpmalarına karşı koruma sağlar. M+S (Mud and Snow) sertifikasyonu, kış aylarında bile yük taşıma güvenliği sunar.

**3. Uzun Ömür ve Düşük Aşınma:** Ticari kullanımda maliyet verimliliği kritiktir. Premium kamyonet lastikleri, 80.000-120.000 km ömür sunabilir. Bazı fleet kullanımlarında, regülarlık yapılarak ömür 150.000-180.000 km'ye kadar uzatılabilir. Sert kauçuk bileşimleri, yavaş ama eşit aşınma sağlar. Derin sırt deseni (başlangıç derinliği 10-12 mm), uzun kullanım ömrü garantisi verir. Wear indicator (aşınma göstergeleri), yasal limit olan 1.6 mm öncesinde uyarı verir.

**4. Yakıt Ekonomisi ve Düşük Yuvarlanma Direnci:** Modern kamyonet lastiklerinde low rolling resistance (düşük yuvarlanma direnci) teknolojisi kullanılır. EU tire labeling sisteminde A-B sınıfı yakıt verimliliği puanları, yıllık 500-1000 litre yakıt tasarrufu sağlayabilir. Silica katkılı kauçuk karışımları, hem grip hem yakıt ekonomisi dengesini optimize eder. Aerodinamik yan duvar tasarımı, hava direncini azaltır. Hafif ama güçlü malzemeler, toplam araç ağırlığını düşürür.

**5. Çok Yönlü Kulanım Desenleri:** Kamyonet lastikleri, kullanım alanına göre farklı sırt desenleriyle üretilir: **Highway Pattern** - %80 asfalt kullanım için simetrik desenler, sessiz ve konforlu sürüş, şehiriçi dağıtım için ideal. **Mixed Service Pattern** - %50 asfalt + %50 toprak/şantiye kullanımı için tasarlanmış, inşaat, tarım, servis araçları için uygun. **All-Terrain Pattern** - Zorlu arazi koşulları için agresif desenler, off-road çekiş kabiliyeti, bölgesel kargo taşımacılığı için.

**6. Fren Performansı ve Güvenlik:** Yüklü bir kamyonet, boş ağırlığına göre %40-60 daha ağırdır ve bu, fren mesafesini önemli ölçüde etkiler. Modern kamyonet lastikleri, ıslak zeminde kısa fren mesafesi sağlayacak şekilde tasarlanmıştır. EU tire labeling sisteminde A-B sınıfı ıslak fren performansı, 80 km/h'den tam durma için 3-6 metre fark yaratabilir. Aquaplaning direnci, yağışlı havalarda yük taşırken kritik öneme sahiptir. Geniş çevre kanalları ve siping teknolojisi, su tahliyesini optimize eder.

**7. Mevsimsel Seçenekler:** **Yaz Lastikleri:** 7°C üzeri sıcaklıklarda optimum performans, sert kauçuk karışımı ile yüksek ömür. **Kış Lastikleri:** 3PMSF (Three Peak Mountain Snowflake) sembolü ile sertifikalı, -10°C ile +7°C arası sıcaklıklarda üstün performans. **4 Mevsim Lastikleri:** Yıl boyu kullanım rahatlığı, M+S işareti ile kış güvenliği, orta iklim bölgeleri için ideal.

**Popüler Kamyonet Lastik Serileri:**

**Michelin Agilis:** Agilis 3, Agilis CrossClimate (4 mevsim), Agilis Alpin (kış) varyantlarıyla sektörün lideri. Duratech teknolojisi ile %25 daha uzun ömür, MaxTouch Construction ile eşit aşınma. 10 yıl/120.000 km garanti programı. Fleet müşterileri için özel fiyatlandırma.

**Continental VanContact:** VanContact 100, VanContact A/S (all-season), VanContact Eco seçenekleri. Black Chilli kauçuk karışımı ile her mevsim grip, Cool Sidewall teknolojisi ile yüksek yük altında düşük ısınma. ContiSeal teknolojisi ile 5mm'ye kadar delik otomatik tamir.

**Bridgestone Duravis:** Duravis R660, Duravis Van (ekonomik) modelleri. NanoPro-Tech teknolojisi ile %15 daha uzun ömür, Optimized Contact Patch ile eşit temas ve aşınma. Kurumsal müşteriler için Tire Management System hizmeti.

**Goodyear EfficientGrip Cargo:** Yeni nesil ticari lastik, A sınıfı yakıt ekonomisi ve B sınıfı ıslak fren performansı. Fuel Saving Technology ile 1.5 litre/100km tasarruf potansiyeli. Extended Mileage Service garantisi.

**Pirelli Carrier:** Carrier, Carrier All Season, Carrier Winter serileri. High Mileage Compound ile 100.000 km+ ömür, Reinforced Structure ile yüksek yük güvenliği. Nokian Ağır Vasıta Test merkezinde onaylanmış performans.

**Kamyonet Lastik Fiyatları:**

**Ekonomik Segment (₺1500-2500):** Petlas Full Power PT825, Lassa Transway, Maxxis Vansmart gibi yerli ve Uzak Doğu markaları. Küçük işletmeler ve düşük kilometreli kullanımlar için uygun maliyet. 60.000-80.000 km ömür beklentisi.

**Orta Segment (₺2500-4000):** Hankook Vantra, Kumho Portran, Nexen Roadian CT8. OEM kalitesinde performans, orta düzey fleet kullanımı için ideal. 80.000-100.000 km ömür, makul birim maliyet.

**Premium Segment (₺4000-6500):** Michelin, Continental, Bridgestone, Goodyear, Pirelli global markaları. Maksimum ömür, güvenlik, yakıt tasarrufu. 100.000-120.000 km ömür, km başına en düşük maliyet. Fleet yönetimi için telematics entegrasyonu.

**Kamyonet İçin Lastik Seçim Kılavuzu:**

**Kullanım Tipi Analizi:** Şehir içi dağıtım (günde 100-200 km, sık durma-kalkma) için: Yumuşak kauçuk, iyi fren performansı, sessiz lastikler öncelikli. Şehirlerarası nakliye (günde 300-500 km, otoyol ağırlıklı) için: Uzun ömürlü, düşük yuvarlanma dirençli, yakıt tasarruflu lastikler. Karma kullanım (şehir + arazi, inşaat vs.) için: Güçlü yan duvar, mixed service desenli, dayanıklı lastikler önerilir.

**Yük Durumu Değerlendirmesi:** Sürekli tam yük taşıyan araçlar için maximum load capacity (maksimum yük indeksi) gereklidir. Yarı yük veya değişken yük taşıyanlar için biraz daha düşük indeks yeterlidir, fakat basınç ayarlaması önemlidir. Römork çeken kamyonetler için, arka aksta daha yüksek yük indeksli lastik kullanılmalıdır.

**Kilometre ve Maliyet Hesabı:** Yıllık 40.000+ km yapan araçlar için premium lastikler, km başı maliyet açısından daha avantajlıdır. Yıllık 20.000 km altı için orta segment lastikler optimal maliyet sağlar. Fleet araçlar için toplu alımda %15-25 indirimler ve ücretsiz bakım hizmetleri mevcuttur.

**Ebat Seçimi:** Küçük kamyonetler (Fiat Fiorino, Citroen Berlingo): 195/65 R15 C, 205/65 R15 C. Orta kamyonetler (Ford Transit Connect, VW Caddy): 215/65 R16 C, 205/75 R16 C. Büyük kamyonetler (Ford Transit, Mercedes Sprinter): 215/75 R16 C, 225/75 R16 C, 235/65 R16 C.

Sakarya Lastik olarak, ticari araç sahipleri ve filolar için özel hizmetlerimiz: Toplu alımlarda kurumsal indirim, ödeme kolaylıkları, ücretsiz lastik rotasyonu (her 10.000 km), basınç kontrol hizmeti, roadside assistance anlaşmaları ve lastik yaşam döngüsü yönetimi hizmeti sunuyoruz.`,
                keywords: ['kamyonet lastiği', 'kamyonet lastik fiyatları', 'ticari araç lastiği', 'c işaretli lastik', 'ticari lastik', 'hafif ticari lastik']
            },
            '4x4': {
                title: '4x4 Arazi Lastikleri',
                content: `4x4 arazi lastikleri, off-road (yol dışı) kullanım için özel olarak tasarlanmış, extreme çekiş gücü, maksimum dayanıklılık ve çok yönlü performans sunan lastiklerdir. Jeep Wrangler, Toyota Land Cruiser, Land Rover Defender, Suzuki Jimny, Mitsubishi Pajero gibi gerçek 4x4 araçlar için üretilen bu lastikler, hem zorlu arazi koşullarında hayat kurtarıcı performans hem de günlük kullanımda kabul edilebilir konfor dengesi sağlar.

Modern 4x4 lastikleri, sadece off-road meraklıları için değil, kış mevsiminde dağ yollarını kullanan, inşaat ve tarım alanlarında çalışan, macera turizminde aktif olan geniş bir kullanıcı kitlesine hitap eder. Türkiye'nin değişken coğrafyası ve zorlu iklim koşulları göz önüne alındığında, doğru 4x4 lastik seçimi hem güvenlik hem de performans açısından kritik önem taşır.

**4x4 Lastik Kategorileri ve Kullanım Alanları:**

**1. Highway Terrain (H/T) - Karayolu Odaklı:** %80 asfalt + %20 hafif arazi kullanımı için tasarlanmış 4x4 lastiklerdir. Şehir içinde sıklıkla kullanılan, hafta sonları hafif arazi aktivitelerine katılan araçlar için idealdir. Düşük yuvarlanma direnci ile yakıt ekonomisi sağlar (EU labeling B-C sınıfı). Sessiz sürüş için optimize edilmiş sırt deseni (68-72 dB aralığı). Yağmurlu ve karlı yollarda yeterli güvenlik. Örnekler: Continental CrossContact LX, Michelin Latitude Tour, Bridgestone Dueler H/L. Avantajları: Uzun ömür (70.000-90.000 km), konforlu sürüş, makul fiyat. Dezavantajları: Ağır arazide sınırlı performans, derin çamurda yetersiz çekiş.

**2. All-Terrain (A/T) - Çok Amaçlı:** %60 asfalt + %40 arazi dengesi sunan, en popüler 4x4 lastik kategorisidir. Hem günlük işe gidip gelme hem de hafta sonu off-road maceralar için uygundur. Agresif sırt deseni ile toprak, çakıl, çamur ve karla başa çıkabilir. M+S (Mud and Snow) sertifikasyonu, bazı modellerde 3PMSF kar performans sembolü. Güçlendirilmiş yan duvarlar, kaya ve taş çarpmalarına dayanıklı. Örnekler: BF Goodrich All-Terrain T/A KO2, Goodyear Wrangler All-Terrain Adventure, Continental TerrainContact A/T, Yokohama Geolandar A/T G015. Avantajları: Çok yönlülük, makul gürültü seviyesi (72-75 dB), iyi dayanıklılık (60.000-80.000 km). Dezavantajları: Asfalt konforu H/T'den düşük, yakıt tüketimi biraz yüksek, extreme arazide M/T'den zayıf.

**3. Mud-Terrain (M/T) - Çamur ve Ağır Arazi:** %40 asfalt + %60 extreme arazi için tasarlanmış, maksimum çekiş gücü sunar. Derin ve agresif sırt deseni (16-20 mm derinlik), çamur, kum, kayalık arazide üstün performans. Geniş aralıklı bloklar, self-cleaning (kendini temizleme) özelliği sağlar. Extra güçlü yan duvarlar, 3-ply sidewall construction, kaya tırmanışlarında kritik koruma. Stone ejector teknolojisi, sırt deseninde taş sıkışmasını önler. Örnekler: BF Goodrich Mud-Terrain T/A KM3, Goodyear Wrangler MT/R Kevlar, Mickey Thompson Baja MTZ P3, Cooper Discoverer STT Pro. Avantajları: Extreme arazi performansı, maksimum dayanıklılık, agresif görünüm. Dezavantajları: Gürültülü (75-78 dB), yüksek yakıt tüketimi, asfalt konforu düşük, daha kısa ömür (50.000-70.000 km), yüksek fiyat.

**4. Rugged-Terrain (R/T) - Hibrid Kategori:** A/T ile M/T arası konumlanmış, son yılların yeni kategorisidir. Agresif görünüm ve iyi off-road performansı, M/T'ye göre daha sessiz ve konforlu. Örnekler: BF Goodrich Rugged-Terrain T/A, Nitto Ridge Grappler. %50 asfalt + %50 arazi dengesi arayanlar için ideal orta yol.

**5. Rock-Terrain (Extreme Off-Road):** Profesyonel rock crawling ve extreme 4x4 yarışları için özel lastikler. Çok yumuşak yan duvarlar (bias-ply construction), ultra düşük basınçta çalışabilme yeteneği. Sticky compound (yapışkan kauçuk) ile kayalarda maksimum tutuş. Günlük kullanıma uygun değil, sadece off-road parkurlarda kullanılır.

**4x4 Lastik Teknik Özellikleri:**

**Yan Duvar Gücü:** 4x4 lastiklerinde yan duvar teknolojisi hayati önem taşır. 3-Ply Sidewall Construction: Üç katlı güçlendirme, kaya ve taş çarpmalarına karşı koruma. Kevlar Reinforcement: BF Goodrich ve Goodyear'ın kullandığı, ekstra dayanıklılık sağlayan Kevlar şeritler. Armor-Tek3: Cooper'ın geliştirdiği, 3 katlı çelik ve naylon güçlendirme sistemi. Bead Armor: Jant kenarı bölgesinde ekstra koruma tabakası, rim guard hasarlarını önler.

**Ebat ve Ölçüler:** 4x4 lastikleri genellikle iki farklı ölçü sisteminde ifade edilir: Metric (205/70 R15): Avrupa standardı, genişlik/yan duvar oranı/çap. Flotation (31x10.50 R15 LT): Amerikan standardı, toplam çap x genişlik x jant çapı + Light Truck (LT) işareti. Örnek: 31x10.50 R15 = 31 inch (79 cm) dış çap, 10.50 inch (27 cm) genişlik, 15 inch jant.

**Load Range ve Ply Rating:** Load Range C (6-ply): Orta yük kapasitesi, binek 4x4'ler için yeterli. Load Range D (8-ply): Ağır 4x4'ler ve hafif kamyonetler için. Load Range E (10-ply): Maksimum yük kapasitesi, ağır römork çekme ve modifiyeli araçlar için. Higher ply rating, daha yüksek basınç kapasitesi ve dayanıklılık anlamına gelir.

**Compound (Kauçuk Karışımı):** Silica-enhanced compound: Islak zeminde grip, düşük sıcaklıkta esneklik. Cut-and-chip resistant: Kesik ve yırtılmaya karşı dirençli formülasyon. Long-wearing tread compound: Uzun ömür için optimize edilmiş sertlik dengesi.

**Popüler 4x4 Lastik Modelleri:**

**BF Goodrich All-Terrain T/A KO2:** Sektörün efsanesi, 1976'dan beri sürekli geliştirilmiş. CoreGard Technology ile %20 daha dayanıklı yan duvarlar. 3-Peak Mountain Snowflake (3PMSF) kar performans sertifikası. Serrated shoulder design, çamur ve kardan çıkışta ekstra çekiş. 50.000 mil (80.000 km) garanti.

**Goodyear Wrangler MT/R Kevlar:** Kevlar takviyeli yan duvarlar, extreme dayanıklılık. Armor-Max Technology, yan duvar kesilmelerine karşı %35 daha fazla direnç. Self-cleaning shoulder blocks, çamurda maksimum performans. Durawall Technology, jant koruma sistemi.

**Cooper Discoverer STT Pro:** Profesyonel seviye M/T lastik, rekabetçi fiyat. Armor-Tek3 Construction, üç katlı yan duvar koruması. Silica-enhanced tread compound, ıslak zeminde 25% daha iyi fren performansı. Stone ejector ribs, taş sıkışmasını önler.

**Yokohama Geolandar X-AT:** Hybrid A/T-M/T kategori, agresif ama sessiz. GEO-SHIELD Technology, çok katlı karkas koruması. Triple 3D sipes, kar ve buzda tutuş. Mud & Snow + 3PMSF çift sertifikasyon.

**4x4 Lastik Fiyatları:**

**Ekonomik Segment (₺2000-3500):** Maxxis Bravo, Hankook Dynapro MT, Kumho Road Venture. Hobi seviyesi off-road kullanıcılar için uygun maliyet. 15-16 inch ebatlar bu segmentte baskın.

**Orta Segment (₺3500-6000):** Yokohama Geolandar, Cooper Discoverer, Falken Wildpeak. OEM kalitesi, dengeli performans. 16-17 inch ebatlar yaygın.

**Premium Segment (₺6000-10000):** BF Goodrich, Goodyear, General Tire Amerika markları. Proven off-road performance, uzun garanti. 17-18 inch performans ebatları.

**Extreme Segment (₺10000+):** Mickey Thompson, Toyo Open Country, Nitto profesyonel serileri. Yarış kalitesi, özel sipariş ebatlar (33", 35", 37" ve üzeri). LT işaretli maksimum yük kapasiteli modeller.

**4x4 Lastik Seçim Rehberi:**

**Kullanım Oranı Analizi:** Ağırlıklı şehir (%70+): H/T seçin, konfor ve ekonomi öncelikli. Dengeli kullanım (%50-50): A/T ideal, çok yönlülük. Ağırlıklı arazi (%60+): M/T gerekli, performans odaklı.

**Araç Tipi:** Compact 4x4 (Jimny, Vitara): 215/70 R16, 205/70 R15 ebatlar, A/T veya H/T yeterli. Orta 4x4 (Wrangler, Bronco): 245/75 R17, 255/75 R17 ebatlar, A/T veya R/T önerili. Ağır 4x4 (Land Cruiser, Patrol): 285/70 R17, 265/70 R18 ebatlar, premium A/T veya M/T.

**Modifikasyon Durumu:** Stock araçlar: OEM recommended size (fabrika önerisi), Load Range C-D yeterli. Lifted araçlar: +1-2 inch büyük ebat, Load Range D-E önerili, regear (dişli oranı değişikliği) gerekebilir.

Sakarya Lastik olarak, 4x4 tutkunları için özel hizmetlerimiz: Off-road lastik uzmanlığı, lift kit sonrası ebat danışmanlığı, aggressive alignment ayarları, aired-down (düşük basınç) off-road kullanımı sonrası kontrol ve profesyonel balans hizmetleri sunuyoruz.`,
                keywords: ['4x4 lastiği', 'arazi lastiği', 'off-road lastik', 'mud terrain', 'all terrain lastik', '4x4 lastik']
            },
            'Kamyon': {
                title: 'Kamyon Lastikleri',
                content: `Kamyon lastikleri (Truck Tires), ağır ticari araçlar için özel olarak mühendislik yapılmış, extreme yük taşıma kapasitesi, maksimum dayanıklılık ve düşük işletme maliyeti sağlayan profesyonel lastiklerdir. Mercedes Axor, MAN TGX, Scania R-Series, Volvo FH, Ford Cargo, BMC Pro, Isuzu gibi ağır tonajlı taşıma araçları için tasarlanmış bu lastikler, lojistik şirketlerinin en önemli maliyet kalemi ve operasyonel verimliliğin kilit bileşenidir.

Bir kamyon filosu için lastik maliyeti, toplam işletme giderlerinin %15-20'sini oluşturur ve doğru lastik seçimi, yıllık milyonlarca lira tasarruf anlamına gelebilir. Modern kamyon lastik teknolojileri, sadece dayanıklılık değil, yakıt verimliliği, sürüş güvenliği, düşük emisyon ve sürdürülebilirlik gibi çok yönlü faydalar sunar. Avrupa'da "truck tire management systems" (kamyon lastik yönetim sistemleri) ile donatılmış filolar, %25-30 oranında lastik maliyeti düşürmeyi başarabilmektedir.

**Kamyon Lastiği Aks Pozisyonlarına Göre Tasarım:**

**1. Steer (Ön Aks) Lastikleri:** Araç kontrolü ve yön verme için kritik lastiklerdir. Ribbed (çizgili) sırt deseni, düz kanallar halinde uzanan desenler. Excellent steering response (mükemmel direksiyon tepkisi), hassas manevra kabiliyeti. Low rolling resistance (düşük yuvarlanma direnci), yakıt ekonomisine direkt katkı. Düz aşınma profili için özel compound (kauçuk karışımı). Yüksek hız stabilitesi (otoyol kullanımı için 130+ km/h). Load index 144-156 aralığı (2500-4000 kg/lastik). Örnek Modeller: Michelin X Multi Z, Continental HSR2, Bridgestone R249, Goodyear KMAX S. Regüler yapılamaz, aşındığında değiştirilir. Ortalama ömür: 120.000-180.000 km.

**2. Drive (Tahrik Aks) Lastikleri:** Motorun gücünü yola ileten, çekiş sağlayan lastiklerdir. Block pattern (blok desen), agresif ve derin sırt deseni (%15-18 mm başlangıç derinliği). Maximum traction (maksimum çekiş gücü), yokuş çıkışlarında kritik. Open shoulder design, çamur ve kar atma özelliği. Yüksek torque resistance (tork direnci), ağır yük altında güç aktarımı. Retreadable casing (regüler yapılabilir karkas), 2-3 kez yeniden kullanım potansiyeli. Load index 152-160 (3550-4500 kg/lastik). Örnek Modeller: Michelin X Multi D, Continental HDR2, Bridgestone M716, Goodyear KMAX D. Regüler yapılabilir, toplam ömür: 300.000-500.000 km (orijinal + 2-3 regüler).

**3. Trailer (Römork/Dorse) Lastikleri:** Yük taşıyan ama çekiş vermeyen, römork ve portelerde kullanılan lastiklerdir. Rib pattern (çizgili desen), düşük yuvarlanma direnci için optimize edilmiş. Energy efficient (enerji verimli), yakıt tüketimini minimize eder. Eşit basınç dağılımı, uzun ömür sağlar. Load index 146-156 (2650-4000 kg/lastik). Retreadable, 2-3 kez regüler yapılabilir. Örnek Modeller: Michelin X Multi T, Continental HTR2, Bridgestone R168, Goodyear KMAX T. Wide base (geniş taban) single tire seçeneği, double montaj yerine tek lastik kullanımı (örnek: 455/45 R22.5). Ortalama ömür: 150.000-250.000 km (orijinal), toplam 400.000-600.000 km (regülerlerle).

**4. All-Position (Çok Amaçlı) Lastikler:** Tüm akslarda kullanılabilecek şekilde tasarlanmış, küçük filolar için pratik çözümdür. Compromise performance, her alanda iyi ama specialized lastikler kadar üstün değil. Stok yönetimi kolaylığı, tek tip lastik kullanımı. Orta derinlikte sırt deseni (12-14 mm). Örnek: Michelin X Multi, Continental Hybrid HS3/HD3, Goodyear KMAX. Regüler yapılabilir ama specialized lastikler kadar verimli değil.

**Kamyon Lastiği Teknik Özellikleri:**

**Ebat Standardı:** Kamyon lastikleri genellikle metric radial (metrik radyal) sisteminde ifade edilir: 315/80 R22.5 156/150L şeklinde. 315 mm genişlik, 80 aspect ratio (yan duvar yüksekliği/genişlik oranı), R radial construction, 22.5 inch jant çapı. 156/150 dual load index (tek/çift montaj yük indeksi), L speed rating (120 km/h).

**TL / TT Tipler:** TL (Tubeless - İç lastiksiz): Modern standart, hava kaybı durumunda daha güvenli, montaj daha kolay. TT (Tube Type - İç lastikli): Eski tip, bazı gelişmekte olan pazarlarda hala kullanılır, jant hasarlarında iç lastik koruma sağlar.

**Ply Rating ve Yük Kapasitesi:** Kamyon lastikleri 14-22 ply rating (kat sayısı) arasında değişir. Her ply artışı, +200-300 kg yük kapasitesi anlamına gelir. Load index 140+ standard (140 = 2500 kg, 150 = 3350 kg, 160 = 4500 kg). Overload resistant construction, aşırı yük durumlarında güvenlik.

**Casing Construction (Karkas Yapısı):** Steel-belted radial (çelik şeritli radyal), modern kamyon lastiklerinin standardı. Multiple ply layers (çok katlı takviye), 4-6 çelik şerit + 2-3 naylon şerit. Bead wire construction (tel halka yapısı), jant oturma güvenliği. Retreadability (regülerlik), casing quality ile doğrudan ilişkili.

**Retreading (Regüler Lastik) Sistemi:** Kamyon lastiklerinin en önemli maliyet avantajı, 2-3 kez regüler yapılabilme özelliğidir. Hot-cap process: Eski sırt tamamen sökülür, yeni sırt ısıtılarak yapıştırılır, ekonomik ama kalite değişken. Cold process (pre-cure): Önceden üretilmiş sırt şeridi soğuk yapıştırma ile monte edilir, yüksek kalite, uzun ömür. Cost savings: Yeni lastiğe göre %50-60 daha ucuz regüler lastik. Fleet operations için kritik, TCO (Total Cost of Ownership) %25-30 düşürebilir. Türkiye'de Bridgestone Bandag, Michelin Remix gibi OEM regüler programları mevcuttur.

**Yakıt Verimliliği Teknolojileri:** Modern kamyon lastikleri, %5-8 yakıt tasarrufu sağlayabilir. EU tire labeling sisteminde A-B sınıfı lastikler, 100.000 km'de 2000-3000 litre yakıt tasarrufu anlamına gelir. Low rolling resistance compounds, silica katkılı özel kauçuk karışımları. Optimized tread depth, gereksiz sırt derinliği yuvarlanma direncini artırır. Lightweight construction, composite materials ile ağırlık azaltma.

**Akıllı Lastik Teknolojileri:** TPMS (Tire Pressure Monitoring System): Basınç ve sıcaklık sensörleri, gerçek zamanlı izleme. RFID chips: Lastik kimlik çipi, fleet management entegrasyonu, lastik geçmişi takibi. Telematics integration: Aşınma tahmini, bakım planlaması, AI-based optimization. ContiConnect gibi sistemler, lastiğin tüm yaşam döngüsünü yönetir.

**Popüler Kamyon Lastik Markaları:**

**Michelin (Fransız - Premium):** X Multi serisi (Z ön, D tahrik, T römork), endüstri standardı. X Line Energy serisi, maksimum yakıt tasarrufu (A sınıfı). Remix regüler programı, OEM kalitesi garantili. Fleet solutions: Michelin Connected Fleet ile tam dijital yönetim. 10 yıl/1.000.000 km toplam garanti programları.

**Continental (Alman - Premium):** Hybrid serisi (HS3 ön, HD3 tahrik, HT3 römork), yüksek teknoloji. EcoPlus teknolojisi, %20-25 daha uzun ömür. ContiPressureCheck sistemi, entegre sensör teknolojisi. ContiRe regüler programı, cold-cure yüksek kalite. Conti360° Fleet Services, dijital fleet yönetim platformu.

**Bridgestone (Japon - Premium):** R-series profesyonel lastikler (R249 ön, M716 tahrik, R168 römork). Fuel Tech compound, düşük yakıt tüketimi. NanoPro-Tech, grip ve ömür optimizasyonu. Bandag regüler sistemi, dünyanın en büyük retread ağı. Bridgestone Fleet Care, fleet management hizmetleri.

**Goodyear (Amerikan - Premium):** KMAX serisi (S ön, D tahrik, T römork), global lider. Fuel Max Technology, EU labeling A-B sınıfı. Duragrip Technology, 20% daha uzun ömür. TreadMax regüler programı, garanti kapsamında. Total Mobility paket, road assistance + lastik yönetimi.

**Petlas / Lassa (Türk - Ekonomik/Orta):** Yerli üretim avantajı, hızlı tedarik. Petlas RH100, SH100 profesyonel seriler. Lassa LT/R ve LD/R kamyon lastikleri. Türkiye şartlarına optimize edilmiş desenler. %30-40 daha uygun fiyat, orta ömür (80.000-150.000 km).

**Hankook / Kumho (Kore - Orta Segment):** DH series (Hankook), KRD series (Kumho). OEM kalitesi, rekabetçi fiyat. Türkiye'de artan pazar payı, güçlü bayi ağı. Premium markalara alternatif, %20-25 daha ucuz.

**Kamyon Lastik Fiyatları (2024):**

**Ekonomik Segment (₺5000-8000/lastik):** Petlas, Lassa, Çin markaları (Aeolus, Triangle). Küçük filolar ve düşük kilometreli kullanım. Regüler yapılamaz veya düşük kalite regüler. TCO (Total Cost of Ownership) yüksek, km başı maliyet ₺0.08-0.10.

**Orta Segment (₺8000-12000/lastik):** Hankook, Kumho, Nexen, Maxxis. OEM kalitesi, 1-2 kez regüler yapılabilir. Orta ölçekli filolar için uygun. TCO orta seviye, km başı maliyet ₺0.06-0.08.

**Premium Segment (₺12000-18000/lastik):** Michelin, Continental, Bridgestone, Goodyear. 2-3 kez premium regüler yapılabilir (her biri ₺4000-6000). Büyük filolar ve long-haul kullanımı için ideal. TCO en düşük, km başı maliyet ₺0.04-0.06. Toplam yaşam döngüsü maliyeti (orijinal + 2-3 regüler) dikkate alındığında en ekonomik seçenek.

**Kamyon Lastik Seçim Kılavuzu:**

**Kullanım Analizi:** Şehir içi dağıtım (urban delivery): Sık durma-kalkma, düşük hız, ön aks aşınması yüksek. Steer ve drive lastiklere öncelik, trailer ömrü daha uzun. Şehirlerarası nakliye (long haul): Yüksek km, otoyol ağırlıklı, yakıt verimliliği kritik. Düşük yuvarlanma dirençli, A-B sınıfı lastikler. Karma kullanım (regional): %50 şehir + %50 otoyol, all-position lastikler pratik. Dengeli maliyet ve performans.

**Fleet Size Analizi:** 1-5 araç (küçük): All-position lastikler ile stok basitleştirme, lokal marka avantajları, maliyet odaklı. 6-20 araç (orta): Aks pozisyonuna göre specialized lastikler, orta segment markalar, regüler başlangıcı. 20+ araç (büyük): Premium markalar, full fleet management, dijital izleme sistemleri, regüler programları, toplam maliyet optimizasyonu (TCO).

**Regüler Stratejisi:** İlk lastik (virgin): Premium marka seçimi, retreadable casing kalitesi kritik. 1. Regüler (60-70% aşınma sonrası): OEM regüler programları (Bandag, Remix, ContiRe). 2. Regüler (ikinci regüler sonrası): Casing kontrolü, ultra-sonic test. 3. Kullanım (bazı lastikler): Sadece casing çok iyi kalitede ise.

**Basınç Yönetimi:** Doğru basınç, lastik ömrünü %20-30 uzatır. Underinflation (düşük basınç): Yan duvar hasarı, yüksek yakıt tüketimi, aşırı ısınma. Overinflation (yüksek basınç): Merkezde hızlı aşınma, sert sürüş, yol tutuşu azalır. TPMS sistemi, automatic monitoring ile optimal basınç. Dual-tire configuration: İç-dış lastik basınçları eşit olmalı, yoksa bir lastik tüm yükü taşır.

Sakarya Lastik olarak, kamyon filolarına özel profesyonel hizmetlerimiz: 7/24 roadside assistance (yol yardım), fleet management danışmanlığı, toplu alımlarda kurumsal indirim ve vade seçenekleri, ücretsiz lastik rotasyonu ve basınç kontrolü, regüler lastik tedarik ve uygulama, dijital fleet tracking entegrasyonu ile hizmetinizdeyiz.`,
                keywords: ['kamyon lastiği', 'ağır vasıta lastiği', 'tır lastiği', 'kamyon lastik fiyatları', 'regüler lastik', 'truck tire']
            }
        },

        // Marka bazlı içerikler
        brands: {
            'Michelin': {
                title: 'Michelin Lastik',
                content: `Michelin, 1889 yılında Fransa'nın Clermont-Ferrand şehrinde Édouard ve André Michelin kardeşler tarafından kurulmuş, 135 yıllık köklü geçmişiyle dünyanın en prestijli ve yenilikçi lastik markasıdır. Bibendum (Michelin Adam) maskotu ile tanınan marka, sadece lastik üreticisi değil, aynı zamanda Michelin Guide (yıldızlı restoran rehberi) ile gastronomi dünyasının da önemli isimlerinden biridir. Yıllık 200 milyon lastik üretimiyle global lider konumundadır.

Michelin'in marka felsefesi "A Better Way Forward" (Daha İyi Bir Yol) sloganıyla özetlenir: Güvenlik, uzun ömür, yakıt verimliliği ve çevre sorumluluğu asla taviz verilmeyen değerlerdir. Michelin lastikleri, premium segment'te %40-50 daha yüksek fiyata rağmen, toplam sahip olma maliyeti (TCO) hesaplandığında orta segment lastiklerle rekabet edebilecek düzeyde avantaj sunar. Bu avantaj, %20-30 daha uzun ömür, %15-20 daha iyi yakıt ekonomisi ve üstün güvenlik performansından gelir.

**Michelin'in Çığır Açan Teknolojileri:**

**1. EverGrip Technology (Sürekli Tutuş Teknolojisi):** Michelin'in devrim niteliğindeki patenti, lastiğin aşınmasıyla birlikte yağmur performansının azalması sorununu çözer. Geleneksel lastiklerde sırt derinliği azaldıkça su tahliye kapasitesi düşer ve aquaplaning riski artar. EverGrip teknolojisinde, sırt bloklarının içine yerleştirilmiş "expanding rain grooves" (genişleyen yağmur kanalları) vardır. Lastik aşındıkça bu gizli kanallar açığa çıkar ve su tahliye kapasitesi korunur. Sonuç: İlk günden son güne kadar tutarlı yağmur performansı. Bu teknoloji, Premier ve CrossClimate serilerinde standart olarak sunulur.

**2. MaxTouch Construction (Maksimum Temas Yapısı):** Michelin mühendisleri, lastiğin yol ile temas eden alanını (contact patch) optimize ederek eşit basınç dağılımı sağlar. Bilgisayar simülasyonları ve sonsuz element analizi (FEA) ile geliştirilen bu teknoloji, lastiğin merkez ve yan bloklarının aynı oranda aşınmasını garanti eder. Klasik lastiklerde omuz (shoulder) blokları daha hızlı aşınırken, MaxTouch ile %20-25 daha uzun ve düzenli ömür elde edilir. Ayrıca, optimum temas alanı frenleme mesafesini %8-12 oranında kısaltır.

**3. Green X Technology (Yeşil Teknoloji):** Michelin'in sürdürülebilirlik taahhüdü, sadece slogan değil, somut mühendislik çözümleridir. Green X işaretli lastikler, düşük yuvarlanma direnci (low rolling resistance) için özel silica-kauçuk karışımları kullanır. Bu karışım, grip performansından taviz vermeden yakıt tüketimini %0.2-0.3 litre/100km azaltır. Yıllık 15.000 km yapan bir araç için bu 300-400 litre yakıt ve 800-1000 kg CO2 emisyon tasarrufu anlamına gelir. Michelin, 2050 yılına kadar %100 sürdürülebilir malzemeden lastik üretme hedefini açıklamıştır.

**4. Helio Compound+ (Kış Kauçuk Teknolojisi):** Michelin kış lastiklerinin gizli silahı, sıcaklık değişimlerine uyum sağlayan akıllı kauçuk karışımıdır. -40°C ile +7°C arası tüm sıcaklıklarda elastik kalabilen bu compound, yüksek silica içeriği ve özel reçinelerle güçlendirilmiştir. Rakip markaların kış lastikleri +5°C üzerinde aşırı yumuşayıp hızla aşınırken, Michelin Alpin ve Pilot Alpin serilerinin ömrü %15-20 daha uzundur. Ayrıca, 3PMSF (Three Peak Mountain Snowflake) sertifikasyonunun %30 üzerinde kar performansı sunar.

**5. Acoustic Technology (Gürültü Önleme):** Michelin Primacy ve Pilot Sport serilerinde, lastiğin iç yüzeyine özel poliüretan köpük tabaka yerleştirilir. Bu tabaka, sürüş sırasında oluşan hava rezonansını absorbe eder ve kabin içi gürültüyü 2-3 dB azaltır. İnsan kulağı logaritmik algıladığı için, 3 dB düşüş %50 daha sessiz sürüş hissi demektir. Akustik teknoloji özellikle elektrikli araçlarda (motor sesi olmadığı için lastik gürültüsü daha belirgin) kritik önem taşır. Tesla, BMW i-series, Mercedes EQ gibi premium elektrikli araçların fabrika çıkışı (OEM) lastiği Michelin Primacy'dir.

**Michelin Ürün Serileri ve Kullanım Alanları:**

**Pilot Sport Serisi (Ultra High Performance):** Michelin'in motorsport DNA'sını yansıtan flag-ship ürünüdür. Pilot Sport 4S, 5, Cup 2 varyantlarıyla track-day meraklılarından günlük performans kullanıcılarına kadar geniş kitle kapsar. Dynamic Response Technology ile direksiyon hassasiyeti %10 artırılmış, kuru zeminde 1.30g+ yan ivme değerlerine ulaşabilir. Porsche 911, Ferrari, Lamborghini gibi süper spor araçların OE lastiğidir. Fiyat: ₺4500-8000/lastik. Ömür: 30.000-45.000 km (agresif kullanımda).

**Primacy Serisi (Premium Touring):** Konfor, sessizlik ve uzun ömrün mükemmel dengesi. Primacy 4, Primacy 4+ (Self-Sealing - kendi kendini onaran) ve Primacy MXM4 modelleriyle Mercedes E/S-Class, BMW 5/7 Series, Audi A6/A8 gibi luxury sedan'ların favorisidir. 72.000-85.000 km garanti programı, sessiz sürüş (69-70 dB), A sınıfı yakıt ekonomisi. Fiyat: ₺3500-5500/lastik.

**CrossClimate Serisi (All-Season Premium):** Michelin'in 4 mevsim devrimidir. Klasik 4 mevsim lastiklerinin aksine, hem 3PMSF kar sertifikası hem de yaz lastiği grip performansı sunar. V-shaped (V-biçimli) sırt deseni ve dual-compound (çift kauçuk) teknolojisi ile tüm mevsimlerde üstün performans. Özellikle ılıman iklimlerde kış/yaz değişiminden kaçınmak isteyenler için ideal. CrossClimate 2, Auto Bild 2023'te "en iyi 4 mevsim lastiği" seçildi. Fiyat: ₺3000-5000/lastik. Ömür: 60.000-75.000 km.

**Alpin / Pilot Alpin Serisi (Premium Winter):** Avrupa'nın en çok satan kış lastiği. Alpin 6 (passenger cars) ve Pilot Alpin 5 (SUV/performance) modelleriyle eksi derecelerde mükemmel çekiş, fren ve yol tutuş garantisi. StabiliGrip Technology ile buzda %15 daha kısa fren, Full Silica compound ile -30°C'de bile esnek kalma. ADAC, TÜV, Auto Motor Sport testlerinde sürekli ilk 3'te yer alır. Fiyat: ₺3500-6000/lastik. Ömür: 40.000-55.000 km (kış mevsimi kullanımında).

**Latitude / Defender Serisi (SUV & Truck):** Yüksek yük kapasiteli araçlar için özel tasarım. Latitude Sport 3 (performance SUV), Latitude Tour (luxury SUV), Defender LTX (pickup truck) seçenekleri. Reinforced sidewalls (güçlendirilmiş yan duvarlar), XL load rating, 3-peak snow rating. Porsche Cayenne, Range Rover, BMW X5/X7'nin OE lastiği. Fiyat: ₺4000-7000/lastik. Ömür: 60.000-80.000 km.

**Agilis Serisi (Commercial Van):** Hafif ticari araçlar için profesyonel çözüm. Agilis 3, Agilis CrossClimate (4 mevsim), Agilis Alpin (kış) varyantları. 10-ply rated (10 katlı güçlendirme), yüksek basınç kapasitesi (max 7.0 bar), regüler yapılabilir casing. Ford Transit, Mercedes Sprinter, VW Transporter fleet kullanımında favorilerdir. Fiyat: ₺3500-5500/lastik. Ömür: 80.000-120.000 km. Michelin 10 yıl garanti programı.

**X Multi Serisi (Heavy Truck):** Kamyon ve ağır vasıta için X Multi Z (steer), X Multi D (drive), X Multi T (trailer) aks pozisyonlarına göre optimize edilmiş. Duratech teknolojisi ile %25 daha uzun ömür, Fuel saving compound ile A-B sınıfı yakıt ekonomisi. Remix regüler programıyla 2-3 kez yeniden kullanım, toplam 300.000-500.000 km ömür. Fleet management sistemleriyle entegre. Fiyat: ₺12.000-18.000/lastik (orijinal).

**Michelin Fiyat Aralıkları ve Segment Konumlandırması:**

**Ekonomik Segment (₺2500-3500):** Michelin'in en uygun fiyatlı modelleri (Energy Saver, Primacy 3). Hala orta segment rakiplerden %20-30 pahalı ama Michelin kalitesi sunuyor. 55.000-70.000 km ömür, B sınıfı yakıt ekonomisi.

**Orta-Premium Segment (₺3500-5000):** En popüler aralık. Primacy 4, CrossClimate 2, Alpin 6 gibi güncel teknolojili modeller. Toplam sahip olma maliyeti optimal nokta. 60.000-75.000 km ömür garantisi.

**Premium Segment (₺5000-7000):** Pilot Sport 4S, Primacy 4+ (Self-Seal), Latitude Sport 3 gibi flagship ürünler. Maksimum performans ve teknoloji. OE (Original Equipment) kalitesi. Mercedes, BMW, Audi fabrika çıkışlı.

**Ultra-Premium / Motorsport Segment (₺7000+):** Pilot Sport Cup 2, Cup 2 R (track-only), Pilot Super Sport gibi yarış teknolojili lastikler. Porsche Cup, Ferrari Challenge gibi seriler için. Günlük kullanıma uygun değil ama track performansı zirvede.

**Michelin Seçim Rehberi:**

**Günlük Konfor Arayanlar:** Primacy 4 veya Primacy 4+ (Self-Sealing) - Sessiz, konforlu, uzun ömürlü.

**Performans Severler:** Pilot Sport 4 (normal sürüş), Pilot Sport 4S (agresif/track günleri).

**4 Mevsim Çözümü:** CrossClimate 2 - Hem yaz gripli hem kış sertifikalı, değiştirme zahmetinden kurtarır.

**SUV Sahipleri:** Latitude Sport 3 (performans), Latitude Tour (konfor), Latitude X-Ice (kış).

**Kış Güvenliği Öncelik:** Alpin 6 (otomobil), Pilot Alpin 5 (SUV/performance) - Kar-buz kralı.

**Ticari Kullanım:** Agilis 3 (yaz), Agilis CrossClimate (4 mevsim) - Yüksek yük, uzun ömür.

Sakarya Lastik olarak, tüm Michelin ürün gamını stoklarımızda bulunduruyoruz. Michelin Türkiye yetkili bayisi olarak, orijinal ürün garantisi, ücretsiz lastik sağlığı kontrolü, profesyonel montaj ve balans, ömür boyu rotasyon hizmeti sunuyoruz. Michelin'in Advantage Program üyesi olarak özel kampanya ve fırsatlardan yararlanabilirsiniz.`,
                keywords: ['michelin lastik', 'michelin fiyatları', 'michelin pilot sport', 'michelin primacy', 'michelin crossclimate', 'michelin alpin']
            },
            'Bridgestone': {
                title: 'Bridgestone Lastik',
                content: `Bridgestone, 1931 yılında Japonya'nın Kurume şehrinde Shojiro Ishibashi tarafından kurulan, bugün dünyanın en büyük lastik üreticisindir. Marka ismi kurucunun soyadının çevirisidir: "Ishi" (taş) + "Bashi" (köprü) = Stone Bridge = Bridgestone. Yıllık 190 milyon lastik üretimi, 180+ ülkede satış ağı ve 25 milyar dolar ciroya sahip global devdir. Firestone markasını satın alarak (2000 yılında tam entegrasyon) Amerikan pazarında da lider konuma gelmiştir.

Bridgestone'un marka değeri "Passion for Excellence" (Mükemmeliyet Tutkusu) ve "Serving Society with Superior Quality" (Üstün Kaliteyle Topluma Hizmet) mottolarıyla tanımlanır. Japon "Kaizen" (sürekli iyileştirme) felsefesiyle her detayda mükemmelliği hedefler. Formula 1'in resmi lastik tedarikçisi olması (2011-2010 arası), Olimpiyat Oyunları'nın global ortağı (Tokyo 2020, Paris 2024) ve Indianapolis 500'ün resmi lastiği olması, marka prestijinin en güçlü göstergesidir.

**Bridgestone'un İnovatif Teknolojileri:**

**1. NanoPro-Tech (Nano Teknoloji):** Bridgestone, lastik üretiminde nano düzey malzeme bilimini uygulayan ilk markadır. Kauçuk moleküllerinin karbon siyahı (carbon black) ve silica partikülleriyle etkileşimini nano ölçekte optimize eder. Sonuç: %15-20 daha düşük yuvarlanma direnci (yakıt tasarrufu), %10-15 daha yüksek grip performansı (güvenlik) ve %20-25 daha uzun ömür (ekonomi). Bu üçlü kombinasyon, geleneksel lastik mühendisliğinde "impossible triangle" (imkansız üçgen) olarak bilinirdi - Bridgestone NanoPro-Tech ile bu sınırı aştı.

**2. 3D-M Shaped Sipes (3 Boyutlu Çentikler):** Kış ve 4 mevsim lastiklerinde kullanılan devrim niteliğindeki sipe (çentik) teknolojisi. Geleneksel düz çentikler, lastikte esneklik sağlar ama sırt bloklarını zayıflatır. Bridgestone'un 3D-M shaped (M-harfi biçimli) çentikleri, bloklara kenetlenerek (interlocking effect) hem esnekliği hem de sertliği korur. Sonuç: Kış lastiklerinde %12 daha iyi buz performansı, yaz lastiklerinde ıslak zeminde %8 daha kısa fren mesafesi. Blizzak, Potenza ve Turanza serilerinde standart.

**3. Fuel Saver Sidewall (Yakıt Tasarruflu Yan Duvar):** Çoğu üretici sadece sırt desenine odaklanırken, Bridgestone yan duvar teknolojisine özel önem verir. Optimum gauge (kalınlık) dağılımı ve nanospheres reinforcement (nano-küre takviyesi) ile yan duvarın esnekliği artırılır. Her tekerlek dönüşünde yan duvarın deforme olup tekrar şeklini alması enerji kaybıdır - Fuel Saver Sidewall bu kaybı %20-30 azaltır. 100.000 km'de 500-700 litre yakıt tasarrufu potansiyeli.

**4. Enliten Technology (Hafif Malzeme):** Bridgestone, sürdürülebilirlik hedefleri doğrultusunda lastik ağırlığını azaltma teknolojisi geliştirmiştir. Özel polymers, optimize edilmiş bead wire (tel halka) yapısı ve ince ama güçlü karkas ile %15-20 daha hafif lastikler üretir. Her lastikte 1-2 kg azaltma, araç performansına doğrudan etki eder: Daha düşük yakıt tüketimi, daha iyi ivmelenme, azaltılmış yaylanmamış kütle. Ecopia ve Turanza Eco serileri Enliten ile donatılmıştır. Ayrıca, hafiflik üretim ve lojistikte CO2 emisyonunu da azaltır.

**5. Multi-Cell Compound (Çok Hücreli Kauçuk):** Bridgestone Blizzak kış lastiklerinin gizli silahı. Mikroskobik gözenekli (multi-cell) kauçuk karışımı, buz ve kar yüzeyindeki su filmini emerek lastik-zemin arasındaki doğrudan teması artırır. Geleneksel kış lastikleri su filminde kayabilirken, Multi-Cell compound "kurutur" ve maksimum çekiş sağlar. -20°C'de test edilen Blizzak, rakiplerinden %18 daha kısa fren mesafesi sunar. Ayrıca, compound aşındıkça daha agresif sırt deseni açığa çıkar - "hidden grip" (gizli tutuş) teknolojisi.

**Bridgestone Ürün Portföyü:**

**Turanza Serisi (Premium Touring):** Bridgestone'un konfor ve sessizlik odaklı flag-ship ürünü. Turanza T005, T005A (Acoustic), 6 (en yeni nesil) modelleriyle Mercedes, BMW, Audi, Lexus gibi luxury sedan'ların OE lastiğidir. Resonance Noise Attenuation (rezonans gürültü önleme) teknolojisi ile 68-69 dB sessizlik seviyesi. 3D sipes ile ıslak zeminde A sınıfı performans. Fiyat: ₺3200-5000/lastik. Ömür: 65.000-80.000 km garantili.

**Potenza Serisi (Ultra High Performance):** Bridgestone'un motorsport DNA'sını taşıyan serisi. Potenza Sport (road-legal track tire), RE-71RS (autocross/time attack), Adrenalin RE004 (performans sokak) varyantları. Formula 1 teknolojisi adapte edilmiş: Pulse Groove design (titreşim emme kanalları), asymmetric tread pattern (asimetrik desen), semi-slick options. Porsche 911, Nissan GT-R, Toyota Supra gibi performans araçların tercihi. Fiyat: ₺4500-7500/lastik. Ömür: 35.000-50.000 km (kullanıma bağlı).

**Blizzak Serisi (Winter Specialist):** Kış lastiği segmentinin tartışmasız lideri. Blizzak LM005 (passenger), DM-V3 (SUV), WS90 (North America) ile -40°C ile +7°C arası her koşulda üstün performans. Multi-Cell compound + RC Polymer (regenerative compound) teknolojileri. ADAC 2023 kış lastiği testinde 1. sıra, Auto Bild "Strongly Recommended", TÜV SÜD onaylı. 3PMSF + Ice Grip sertifikasyonu. Fiyat: ₺3500-5500/lastik. Ömür: 45.000-60.000 km (kış sezonu kullanımı).

**Dueler Serisi (SUV & 4x4):** Yüksek performanslı SUV lastikleri. Dueler H/P Sport (highway performance), A/T 001 (all-terrain), M/T 674 (mud-terrain) ile her arazi için çözüm. L.L. Carbon teknolojisi (long-link carbon structure) ile yan duvar gücü %30 artırılmış. Toyota Land Cruiser, Jeep Wrangler, Ford Ranger'ın OE lastiği. Fiyat: ₺4000-8000/lastik (ebata göre). Ömür: 60.000-100.000 km (A/T models).

**Ecopia Serisi (Eco Performance):** Bridgestone'un sürdürülebilirlik odaklı gamı. Ecopia EP300, EP500 (elektrikli araçlar), EP150 (compact cars) modelleri. A sınıfı yakıt ekonomisi, düşük CO2 emisyonu, %50 sürdürülebilir malzeme içeriği (2025 hedefi). Toyota Prius, Honda Insight, Tesla Model 3'ün fabrika lastiği. Fiyat: ₺2800-4200/lastik. Ömür: 70.000-90.000 km.

**Weather Control A005 Evo (All-Season Premium):** Bridgestone'un 4 mevsim yıldızı. Hem 3PMSF kar sertifikası hem de yaz gripli. Cross-season compound + Dragon Skin sidewall teknolojisi. Aquaplaning direnci sınıfının en iyisi (Auto Zeitung 2022). Özellikle ılıman iklim bölgeleri için ideal. Fiyat: ₺3200-4800/lastik. Ömür: 60.000-75.000 km.

**Duravis Serisi (Commercial Van):** Hafif ticari araçlar için profesyonel dayanıklılık. Duravis R660, Van (ekonomik varyant) modelleri. 10-ply reinforcement, yüksek load index (110+), regüler yapılabilir casing. Ford Transit, Fiat Ducato, Mercedes Sprinter fleet kullanımı. Fiyat: ₺3200-5000/lastik. Ömür: 90.000-130.000 km. Bridgestone Fleet Care Program.

**R-Series (Heavy Truck):** Kamyon ve römork lastikleri. R249 (steer), R297 (drive), R168 (trailer) aks pozisyon lastikleri. Fuel-Tech compound ile B sınıfı yakıt ekonomisi, Retread-optimized casing ile 2-3 regüler. Bridgestone Bandag regüler sistemi - dünyanın en büyük ağı. Fiyat: ₺11.000-17.000/lastik (virgin). Toplam ömür: 400.000-600.000 km (regülerlerle).

**Bridgestone Fiyat Segmentleri:**

**Ekonomik (₺2500-3500):** Ecopia EP150, Turanza ER300 gibi önceki nesil modeller. Hala Bridgestone kalitesi, uygun fiyat.

**Orta-Premium (₺3500-5000):** Turanza T005, Weather Control A005, Blizzak LM005 ana akım ürünleri. En çok satan segment.

**Premium (₺5000-7000):** Potenza Sport, Turanza 6, Dueler H/P Sport Plus performans modelleri. OE kalitesi.

**Ultra-Premium (₺7000+):** Potenza RE-71RS, Potenza Race yarış lastikleri. Track-day, autocross, time attack için.

**Bridgestone Global Ortaklıklar ve Prestij:**

- **Formula 1:** 1997-2010 resmi lastik tedarikçisi. Ferrari'nin 5 şampiyonluk lastiği.
- **Indianapolis 500:** 20+ yıldır resmi lastik. Fastest race on Earth.
- **Tokyo 2020 Olimpiyatları:** Worldwide Olympic Partner. Olympic Turanza.
- **FIA World Endurance Championship:** Leman 24 Saat resmi lastiği.
- **MotoGP:** 2002-2015 motosiklet yarışı resmi lastiği.

**Bridgestone Seçim Kılavuzu:**

**Konfor Odaklı Sürücüler:** Turanza T005 veya Turanza 6 - Sessiz, yumuşak, uzun ömürlü.

**Performans Tutkunları:** Potenza Sport - Günlük kullanım + track capability.

**Kış Güvenliği:** Blizzak LM005 (otomobil) veya DM-V3 (SUV) - Kar-buz performans lideri.

**4 Mevsim Çözümü:** Weather Control A005 Evo - Hem sertifikalı kış hem yaz gripli.

**SUV Sahipleri:** Dueler H/P Sport (asfalt), A/T 001 (arazi+asfalt).

**Eco-Conscious Drivers:** Ecopia EP500 - Çevreci, ekonomik, sürdürülebilir.

Sakarya Lastik olarak, Bridgestone yetkili bayisi ve Bandag regüler programı uygulayıcısıyız. Orijinal ürün garantisi, profesyonel montaj, lifetime rotation, Bridgestone Tire Care hizmeti sunuyoruz.`,
                keywords: ['bridgestone lastik', 'bridgestone turanza', 'bridgestone blizzak', 'bridgestone potenza', 'bridgestone dueler']
            },
            'Continental': {
                title: 'Continental Lastik',
                content: `Continental, 1871 yılında Hannover, Almanya'da kurulan, 153 yıllık köklü geçmişiyle Avrupa'nın en büyük lastik üreticisidir. Sadece lastik değil, aynı zamanda otomotiv elektroniği, fren sistemleri ve ADAS (Gelişmiş Sürücü Destek Sistemleri) konularında da dünya lideridir. "Vision Zero" (Sıfır Kaza) hedefiyle, her teknoloji geliştirilirken güvenlik birinci önceliktir.

Continental'in marka değeri "The Future in Motion" sloganıyla özetlenir ve Alman hassasiyeti, precision engineering ve sürekli inovasyon üzerine kuruludur. Volkswagen Grubu (VW, Audi, Porsche, SEAT, Skoda) ile 150 yıllık stratejik ortaklığı, Mercedes-Benz, BMW ile OE (original equipment) anlaşmaları markanın teknik üstünlüğünün en büyük kanıtıdır.

**Continental Öne Çıkan Teknolojileri:**

**Black Chilli Compound:** Continental'in patentli kauçuk karışımı, siyah biber tozu yapısındaki özel karbon molekülleriyle grip ve ömür dengesini optimize eder. Islak zeminde %10, kuru zeminde %8 daha iyi tutuş. PremiumContact, SportContact, WinterContact serilerinin DNA'sı.

**ContiSeal Technology:** Kendi kendini onaran lastik teknolojisi. 5mm'ye kadar deliklerde özel viscoelastic sealant (yapışkan tabaka) deliği anında kapatır. Hava kaybı olmaz, acil dur yap. Özellikle run-flat alternatifi olarak SUV ve premium sedan sahiplerine sunuluyor.

**ContiSilent:** İç yüzeye uygulanan poliüretan köpük tabaka, kabin gürültüsünü 9 dB azaltır. Elektrikli araçlar (motor sesi yok) için kritik. Audi e-tron, BMW iX, Mercedes EQS OE lastiği.

**SSR (Self-Supporting Runflat):** Güçlendirilmiş yan duvarla 80 km/h'de 80 km yol alabilme. Porsche, BMW M serisi standart donanım.

**Continental Ürün Gamı:**

**PremiumContact 6 / 7:** High-performance touring serisi. Asymmetric+ tread pattern, MacroBlock technology, Adaptive Steering response. Mercedes E-Class, BMW 5 Series OE. Fiyat: ₺3800-6000. Ömür: 60.000-75.000 km.

**SportContact 5 / 6 / 7:** Ultra-high performance. SportContact 7 (2023 model) Nürburgring'de lap record kırdı. Audi RS models, Porsche 911 Carrera. Fiyat: ₺5000-9000. Track-ready performance.

**WinterContact TS860 / TS870:** ADAC 2022-2023 birinci kış lastiği. Cool Chilli compound, 3D sipes, snow curve+ technology. -35°C'de test edilmiş. Fiyat: ₺3500-5500. Ömür: 50.000-65.000 km.

**AllSeasonContact / CrossContact LX25:** 4 mevsim güvenliği. Traction Grooves (kar kanalları) + Summer Compound dengesi. 3PMSF sertifikalı. Fiyat: ₺3200-4800. Ömür: 65.000-80.000 km.

**EcoContact 6 / 6Q:** Çevreci serisi. Green Chilli 2.0 compound, düşük yuvarlanma direnci. A sınıfı yakıt ekonomisi. Tesla Model 3/Y, VW ID.3/4 OE lastiği. Fiyat: ₺3000-4500.

**VanContact / ContiVanContact:** Ticari araçlar. 100 / 200 (all-season) / Eco / 4Season seçenekleri. 8-10 ply reinforcement. Ford Transit, Mercedes Sprinter. Fiyat: ₺3500-5500. Ömür: 100.000-140.000 km.

**Continental Prestij ve Başarılar:**
- ADAC Test Winner: 15+ kez (son 5 yılda)
- Auto Bild "Highly Recommended": 20+ ürün
- OE Partner: 90% Alman otomobil markaları
- Conti360° Fleet: Dijital lastik yönetimi pioneer

Sakarya Lastik olarak, Continental yetkili bayisiyiz. Conti-Servis standartlarında hizmet, ContiRe regüler programı uygulayıcısı, 5 yıl garanti.`,
                keywords: ['continental lastik', 'continental premiumcontact', 'continental sportcontact', 'continental wintercontact', 'conti lastik']
            },
            'Pirelli': {
                title: 'Pirelli Lastik',
                content: `Pirelli, 1872'de Milano, İtalya'da Giovanni Battista Pirelli tarafından kurulan, İtalyan şıklığı ve performans tutkusunu lastik teknolojisiyle birleştiren premium markadır. "Power is Nothing Without Control" (Güç, Kontrol Olmadan Bir Hiçtir) sloganıyla tanınır. Formula 1'in 2011'den beri tek lastik tedarikçisi ve ikonik Pirelli Calendar ile moda-sanat dünyasının da önemli ismidir.

**Pirelli Teknoloji Yenilikleri:**

**Cyber Technology:** Sensör entegreli akıllı lastik. Tire pressure, temperature, tread depth (diş derinliği) real-time monitoring. Pirelli Connesso App ile smartphone entegrasyonu. McLaren, Pagani, Bugatti'de standart.

**Seal Inside:** Kendi kendini onaran teknoloji. 4mm'ye kadar delik anında kapatılır. P Zero serilerinde opsiyonel.

**Noise Cancelling System (PNCS):** Sound-absorbing foam layer, kabin gürültüsünü 3 dB azaltır. Audi, BMW, Porsche premium modellerde.

**Run Flat Technology:** Güçlendirilmiş yan duvar ile 80 km menzil. BMW, Mini Cooper OE.

**Pirelli Product Lines:**

**P Zero:** Flag-ship ultra-high performance. P Zero (standard), P Zero Nero (affordable), P Zero Corsa (track), P Zero Trofeo R (semi-slick race). Ferrari, Lamborghini, McLaren P1 OE lastiği. 300+ km/h onaylı. Fiyat: ₺6000-12.000. Supercar DNA.

**Cinturato:** Günlük premium serisi. Cinturato P7 (touring), P7 Blue (eco), P7 All Season. Mercedes C-Class, BMW 3 Series balanced performance. Fiyat: ₺3500-5500. Ömür: 60.000-80.000 km.

**Scorpion:** SUV specialist. Scorpion Verde (eco SUV), Scorpion Verde All Season, Scorpion Winter, Scorpion ATR (all-terrain). Porsche Cayenne, Range Rover, Maserati Levante OE. Fiyat: ₺4500-8000.

**Winter Sottozero 3:** Premium kış lastiği. Asymmetric tread, directional grooves, 3PMSF. Audi Q7, BMW X5 kış güvenliği. Fiyat: ₺4500-7000. Ömür: 40.000-55.000 km.

**Carrier:** Commercial van serisi. Carrier, Carrier All Season, Carrier Winter. Fiat Ducato, Ford Transit. Fiyat: ₺3800-5500.

**Pirelli Formula 1 Heritage:**
- 2011-2024 F1 exclusive supplier
- 400+ Grand Prix victories
- İkonik Paul Ricard renkli lastikler
- Soft (red), Medium (yellow), Hard (white), Intermediate (green), Wet (blue)

**Pirelli Özel Markalar:**

**P Zero World:** Luxury lastik butikleri. Milano, Los Angeles, Monaco, Tokyo flagship mağazaları.

**Pirelli Design:** Fashion collaboration. Limited edition colored sidewalls, custom tread patterns.

**Pirelli Calendar:** 1964'ten beri prestilijli fotoğraf kalenderi. Dünya çapında sadece 20.000 kopya basılır, satılmaz hediye edilir.

Sakarya Lastik'te Pirelli yetkili satış ve servis noktasıyız. Orijinal İtalyan ithalat, Pirelli Performante servisi, Perfect Fit garantisi sunuyoruz.`,
                keywords: ['pirelli lastik', 'pirelli p zero', 'pirelli scorpion', 'pirelli cinturato', 'pirelli sottozero']
            },
            'Goodyear': {
                title: 'Goodyear Lastik',
                content: `Goodyear Tire & Rubber Company, 1898'de Ohio, Akron'da Frank Seiberling tarafından kurulan, Amerika'nın lastik üretim devridir. İkonik Goodyear Blimp (hava gemisi) ile tanınır, NASA Ay arabulusu için lastik geliştirmiştir ve Chuck Yeager'ın ses hızını aşan X-1 uçağının lastikleri Goodyear üretimidir. Dünya'nın %15 lastik pazarına sahip, 140+ ülkede faaliyet gösterir.

**Goodyear Liderlik Teknolojileri:**

**WeatherReactive Technology:** Akıllı kauçuk karışımı, sıcaklığa göre sertlik/esneklik ayarı yapar. Soğukta grip, sıcakta dayanıklılık. Vector 4Seasons Gen-3 serisinde.

**SoundComfort Technology:** Multi-pitch tread blocks + foam layer, gürültü %25 azaltır. Mercedes S-Class, Audi A8 premium OE.

**RunOnFlat / EMT:** Extended Mobility Technology. 80 km menzil, BMW X serisi standard.

**Fuel Max Technology:** Düşük yuvarlanma direnci. B sınıfı yakıt ekonomisi, %15 tasarruf. EfficientGrip serisi.

**Goodyear Ürün Gamı:**

**Eagle F1 Supersport:** Ultra-high performance flag-ship. Asymmetric tread, ActiveBraking Technology. Porsche 911, Corvette C8, BMW M3 OE. 300 km/h rated. Fiyat: ₺5500-9000. Track-ready.

**Eagle F1 Asymmetric 5 / 6:** High-performance touring. Grip Booster compound, Shark Skin Technology (drag reduction). Mercedes AMG, Audi S-Line. Fiyat: ₺4000-6500. Ömür: 50.000-70.000 km.

**EfficientGrip Performance 2:** Eco-touring. FuelSaving Technology, braking distance reducer, B yakıt sınıfı. VW, Skoda, SEAT OE. Fiyat: ₺2800-4200. Ömür: 70.000-90.000 km.

**UltraGrip Performance+ / 9+:** Premium kış. ActiveGrip Technology, 3D-BIS (Block Interlocking System), sponge compound. ADAC "Good" rating. Fiyat: ₺3500-5500. Ömür: 45.000-60.000 km.

**Vector 4Seasons Gen-3:** All-season market leader. WeatherReactive, 3PMSF, Aquatred grooves. Auto Express "Best Buy". Fiyat: ₺3200-4800. Ömür: 60.000-80.000 km.

**Wrangler:** SUV/pickup serisi. Wrangler HP (highway), AT Adventure (all-terrain), MT/R Kevlar (mud-terrain). Jeep Wrangler nameplate partner. Kevlar-reinforced sidewalls. Fiyat: ₺4500-9000.

**Goodyear İkonlar:**

**Goodyear Blimp:** 1925'ten beri hava gemisi filosu. Süper Kupa, Olimpiyatlar havadan coverage.

**Moon Tire:** 1971 Apollo 15 lunar rover için geliştirilen çelik tel lastik. NASA koleksiyonu.

**NASCAR Partnership:** 60+ yıl resmi lastik tedarikçisi. "Goodyear Racing" efsane.

Sakarya Lastik, Goodyear yetkili distribütörü ve TotalMobility servis istasyonudur. Amerikan teknolojisi, Türkiye garantisi sunuyoruz.`,
                keywords: ['goodyear lastik', 'goodyear eagle', 'goodyear efficientgrip', 'goodyear ultragrip', 'goodyear vector', 'goodyear wrangler']
            },
            'Petlas': {
                title: 'Petlas Lastik',
                content: `Petlas, 1976 yılında İzmir Aliağa'da kurulan, Türkiye'nin en köklü ve en büyük lastik üreticisidir. Brisa A.Ş. markası olan Petlas, yıllık 18 milyon lastik üretim kapasitesiyle Avrupa'nın top 10 üreticisi arasındadır. %70'i ihracat, 180+ ülkeye satış, Avrupa ECE-R117 standartları, Türk mühendislik gücünün göstergesidir.

**Petlas Teknoloji ve Kalite:**

Petlas lastikleri, ECE (Avrupa Ekonomik Komisyonu), DOT (ABD Ulaştırma Bakanlığı), ISO 9001, ISO 14001 sertifikalarına sahiptir. Alman TÜV, ADAC testlerinde "satisfactory" notları alır. Brisa R&D merkezi, yılda 100+ yeni desen geliştirir. Eskişehir ve Aksaray test pistleri, her lastiği 50.000+ km test eder.

**Petlas Ürün Gamı:**

**Imperium Serisi (Yaz Performans):** Asymmetric tread, silica compound, A/B sınıfı ıslak fren. Otomobil ve SUV varyantları. Renault, Fiat, Ford OE tedarikçisi. Fiyat: ₺1200-2200. Ömür: 50.000-65.000 km.

**Snowmaster Serisi (Kış Güvenliği):** 3PMSF sertifikalı, directional tread, M+S onaylı. Snowmaster 2, Snowmaster 2 Sport (performans), Snowmaster W651 (ticari) modelleri. Avrupa kış standartlarında. Fiyat: ₺1500-2800. Ömür: 40.000-55.000 km.

**Explero Serisi (SUV):** Reinforced construction, yüksek load index. PT421 (highway), PT431 (all-season), A/T (off-road) varyantları. Hafif ticari ve SUV çift kullanım. Fiyat: ₺1800-3500. Ömür: 60.000-80.000 km.

**Velox Sport (Performance):** Budget-friendly performance. Asymmetric tread, sport compound. Fiyat: ₺1000-1800. Young drivers favorite.

**Full Power / Transway (Commercial):** C-rated van lastikleri. 8-ply reinforcement, 100+ load index. Transit, Sprinter, Ducato uyumlu. Fiyat: ₺1500-2500. Ömür: 80.000-110.000 km.

**Petlas Avantajları:** Yerli üretim (hızlı tedarik), Avrupa kalitesi (uygun fiyat), geniş bayi ağı (Türkiye'de 2000+ bayi), 2 yıl garanti. Premium markalara %40-50 daha uygun, orta segment kalitesi.

Sakarya Lastik, Petlas yetkili bayisidir. Brisa garantisi, orijinal ürün, profesyonel servis sunuyoruz.`,
                keywords: ['petlas lastik', 'petlas snowmaster', 'petlas imperium', 'petlas explero', 'türk lastiği', 'yerli lastik']
            },
            'Lassa': {
                title: 'Lassa Lastik',
                content: `Lassa, Brisa Bridgestone Sabancı Holding ortaklığıyla 1974'te Türkiye'de kurulan, Bridgestone teknolojisi ve Türk mühendislik deneyimini birleştiren hibrit premium markadır. "Lassa Confidence" (Lassa Güven) sloganıyla tanınır. Türkiye pazar lideri, yıllık 12 milyon lastik satışı, %60 domestic market share.

**Lassa Kalite Standardı:**

Bridgestone global standartlarında üretim, Japonya'dan teknoloji transferi, Türkiye'de yerel R&D, ECE-R117 Avrupa onayı. Brisa Aksaray fabrikası Bridgestone Group'un Avrupa üretim hub'ı. Lassa lastikleri 80+ ülkeye ihraç edilir, birçok Avrupa markasının (Renault, Dacia, Fiat) OE tedarikçisidir.

**Lassa Seriler:**

**Driveways Serisi (Yaz Touring):** Sport, Sport 2, Touring varyantları. Asymmetric tread, high-grip compound, sessiz sürüş. Renault Clio, Fiat Egea, VW Polo OE. Fiyat: ₺1400-2400. Ömür: 55.000-70.000 km.

**Snoways Serisi (Kış):** 3PMSF + M+S sertifikasyonu. Snoways 3, Snoways 4 (latest), Era (budget) modelleri. Directional tread, winter compound. Türkiye kış şartlarına optimize. Fiyat: ₺1600-2800. Ömür: 45.000-60.000 km.

**Multiways Serisi (4 Mevsim):** All-season practicality. M+S certified, asymmetric pattern. Yıl boyu kullanım rahatlığı. Fiyat: ₺1500-2600. Ömür: 60.000-75.000 km.

**Competus Serisi (SUV):** H/P (highway performance), H/P 2, A/T (all-terrain), Winter modelleri. Reinforced sidewalls, high load rating. Dacia Duster, Suzuki Vitara OE. Fiyat: ₺2000-3800.

**Transway / Transway 2 (Commercial):** C-rated hafif ticari. Bridgestone Duravis teknolojisi. Fleet operations için dayanıklılık. Fiyat: ₺1800-2800. Ömür: 90.000-120.000 km.

**Lassa Unique Selling Points:** Bridgestone kalitesi Türk fiyatı, local production advantages (hızlı servis, stok bolluğu), Brisa nationwide service network, 2+1 yıl extended warranty option.

Sakarya Lastik, Lassa Platinum Dealer statüsündedir. Brisa-Bridgestone ortak garantisi sunuyoruz.`,
                keywords: ['lassa lastik', 'lassa driveways', 'lassa snoways', 'lassa multiways', 'lassa competus', 'bridgestone türkiye']
            },
            'Hankook': {
                title: 'Hankook Lastik',
                content: `Hankook Tire, 1941'de Güney Kore'de kurulan, bugün dünya'nın 7. büyük lastik üreticisidir. "Driving Emotion" sloganıyla performans ve inovasyon odaklıdır. Yıllık 102 milyon lastik üretimi, Almanya Nürburgring Test Center, Real Madrid, UEFA Europa League sponsorluğuyla global marka.

**Hankook Teknoloji:**

**Kontrol Technology:** Optimum tread design via supercomputer simulation. Grip balance optimization.

**Kinergy Technology:** Low rolling resistance, fuel efficiency focus. Eco product line technology.

**Hankook Ürünler:**

**Ventus Serisi (Performance):** S1 evo3 (ultra-high performance), Prime 4 (touring performance), V12 evo2 (max performance). Porsche Macan, Audi Q5, Mercedes GLC OE. Fiyat: ₺2500-5000. Track-capable street tires.

**Kinergy Serisi (Eco-Touring):** 4S 2 (4 mevsim), Eco 2 (fuel saver), GT (grand touring). Volkswagen, Skoda, Ford OE tedarikçisi. A/B sınıfı yakıt ekonomisi. Fiyat: ₺1800-3200. Ömür: 65.000-85.000 km.

**Winter i*cept Serisi (Kış):** evo3, X, RS2 (SUV) modelleri. 3PMSF + ice grip. ADAC "Good" rating. Fiyat: ₺2200-3800. Ömür: 50.000-65.000 km.

**Dynapro Serisi (SUV/Truck):** HP2 (highway performance), AT2 (all-terrain), MT2 (mud-terrain), i*cept (winter SUV). Hyundai Tucson, Kia Sportage OE. Fiyat: ₺2800-6000.

**Vantra / Smart Flex (Commercial):** LT, ST, Flex AH variants. 8-10 ply rated, commercial durability. Fiyat: ₺2200-3500. Ömür: 100.000-130.000 km.

**Hankook Global Presence:** 90+ ülke, Avrupa'da %12 market share, OE partnerships: Audi, BMW, Ford, Hyundai-Kia, Mercedes, VW. Türkiye'de 1500+ bayi, 5 yıl garanti.

Sakarya Lastik, Hankook yetkili distribütördür. Kore teknolojisi, rekabetçi fiyat, geniş stok sunuyoruz.`,
                keywords: ['hankook lastik', 'hankook ventus', 'hankook kinergy', 'hankook winter icept', 'hankook dynapro']
            },
            'Kumho': {
                title: 'Kumho Lastik',
                content: `Kumho Tire, 1960'ta Güney Kore'de kurulan, dünya'nın 9. büyük lastik üreticisidir. Budget-friendly positioning ama improving quality. Yıllık 68 milyon lastik, 180+ ülke distribüsyonu. Marshall (budget brand) ve Kumho (mainstream) dual branding.

**Kumho Teknoloji:**

**ESCOT Tension Control:** Optimum tire structure, even wear technology.

**3D Dimple Technology:** Sipes içinde 3D yapı, winter grip enhancement.

**Kumho Product Lines:**

**Ecsta Serisi (Performance):** PS91 (ultra-high), PS31 (sport touring), HS52 (high-speed touring). Hyundai N, Kia GT OE. Fiyat: ₺2000-4000. Budget performance.

**Solus Serisi (Touring):** HA32 (all-season), KH27 (economy touring), 4S HA32 (4-season). Volkswagen Polo, Renault Megane segment. Fiyat: ₺1500-2500. Value-for-money champion.

**WinterCraft Serisi (Winter):** WP52 (passenger), WS71 (SUV), Ice (studless ice grip). 3PMSF certified. Fiyat: ₺1800-3200. Budget winter solution.

**Crugen Serisi (SUV):** HP71 (highway performance), Premium (luxury SUV), Road Venture (all-terrain). Kia Sorento, Hyundai Santa Fe segment. Fiyat: ₺2200-4500.

**Portran / Road Venture (Commercial):** KC53, CW51 ticari lastikler. Mid-range commercial durability. Fiyat: ₺1800-2800. Ömür: 80.000-110.000 km.

**Kumho Avantajları:** Aggressive pricing (%20-30 altpremium competitors), improving quality (recent generation better), wide availability (Türkiye'de 1200+ bayi), 4 yıl garanti. Especially attractive for budget-conscious buyers seeking better-than-Chinese quality.

Sakarya Lastik, Kumho yetkili satıcısıdır. Kore mühendisliği, akıllı fiyat, hızlı teslimat sunuyoruz.`,
                keywords: ['kumho lastik', 'kumho ecsta', 'kumho solus', 'kumho wintercraft', 'kumho crugen', 'kumho portran']
            }
        },

        // Mevsim bazlı içerikler
        seasons: {
            'Kış': {
                title: 'Kış Lastikleri',
                content: `Kış lastikleri, 7°C altındaki sıcaklıklarda optimal performans gösterecek şekilde tasarlanmıştır.

**Kış Lastiği Özellikleri:**
- Özel yumuşak lastik karışımı
- Derin sırt desenleri (kar tutma)
- M+S veya 3PMSF sembolü
- Gelişmiş su tahliye kanalları

**Kış Lastiği Ne Zaman Takılmalı?** Hava sıcaklığı 7°C'nin altına düştüğünde (Kasım-Aralık ayları) kış lastiği takılmalıdır.`,
                keywords: ['kış lastiği', 'kar lastiği', 'kış lastik fiyatları']
            },
            'Yaz': {
                title: 'Yaz Lastikleri',
                content: `Yaz lastikleri, 7°C üzerindeki sıcaklıklarda optimal performans gösterir.

**Yaz Lastiği Avantajları:**
- Sert lastik karışımı
- Düşük yuvarlanma direnci
- Yüksek hız performansı
- Uzun ömür

**Yaz Lastiği Ne Zaman:** Hava sıcaklığı 7°C'nin üzerine çıktığında (Mart-Nisan) yaz lastiğine geçilmelidir.`,
                keywords: ['yaz lastiği', 'yaz lastik fiyatları', 'summer tire']
            },
            '4 Mevsim': {
                title: '4 Mevsim Lastikleri',
                content: `4 mevsim lastikleri, hem yaz hem kış koşullarında kullanılabilecek hibrit lastiklerdir.

**4 Mevsim Avantajları:**
- Yıl boyu kullanım
- Maliyet tasarrufu
- Depolama sorunu yok
- M+S sembolü

**Kimler Kullanmalı:** İklimi ılıman bölgelerde yaşayanlar, az km yapanlar için idealdir.`,
                keywords: ['4 mevsim lastiği', 'all season lastik', '4 mevsim lastik fiyatları']
            }
        }
    };

    // SEO İçerik Render Fonksiyonu
    const renderSEOContent = () => {
        if (!products || products.length === 0) return null;

        let contentTitle = '';
        let contentText = '';
        let contentKeywords: string[] = [];

        // Marka + Kategori + Mevsim
        if (selectedBrandName && selectedCategoryName && selectedSeasonName) {
            const brandData = seoContentDatabase.brands[selectedBrandName];
            const categoryData = seoContentDatabase.categories[selectedCategoryName];
            const seasonData = seoContentDatabase.seasons[selectedSeasonName];

            if (brandData && categoryData && seasonData) {
                contentTitle = `${selectedBrandName} ${selectedCategoryName} ${selectedSeasonName} Lastikleri Hakkında`;
                contentText = `${brandData.content}\n\n${categoryData.content}\n\n${seasonData.content}`;
                contentKeywords = [...brandData.keywords, ...categoryData.keywords, ...seasonData.keywords];
            }
        }
        // Marka + Kategori
        else if (selectedBrandName && selectedCategoryName) {
            const brandData = seoContentDatabase.brands[selectedBrandName];
            const categoryData = seoContentDatabase.categories[selectedCategoryName];

            if (brandData && categoryData) {
                contentTitle = `${selectedBrandName} ${selectedCategoryName} Lastikleri Hakkında`;
                contentText = `${brandData.content}\n\n${categoryData.content}`;
                contentKeywords = [...brandData.keywords, ...categoryData.keywords];
            }
        }
        // Marka + Mevsim
        else if (selectedBrandName && selectedSeasonName) {
            const brandData = seoContentDatabase.brands[selectedBrandName];
            const seasonData = seoContentDatabase.seasons[selectedSeasonName];

            if (brandData && seasonData) {
                contentTitle = `${selectedBrandName} ${selectedSeasonName} Lastikleri Hakkında`;
                contentText = `${brandData.content}\n\n${seasonData.content}`;
                contentKeywords = [...brandData.keywords, ...seasonData.keywords];
            }
        }
        // Kategori + Mevsim
        else if (selectedCategoryName && selectedSeasonName) {
            const categoryData = seoContentDatabase.categories[selectedCategoryName];
            const seasonData = seoContentDatabase.seasons[selectedSeasonName];

            if (categoryData && seasonData) {
                contentTitle = `${selectedCategoryName} ${selectedSeasonName} Lastikleri Hakkında`;
                contentText = `${categoryData.content}\n\n${seasonData.content}`;
                contentKeywords = [...categoryData.keywords, ...seasonData.keywords];
            }
        }
        // Sadece Marka
        else if (selectedBrandName) {
            const contentData = seoContentDatabase.brands[selectedBrandName];
            if (contentData) {
                contentTitle = `${contentData.title} Hakkında`;
                contentText = contentData.content;
                contentKeywords = contentData.keywords;
            }
        }
        // Sadece Kategori
        else if (selectedCategoryName && !selectedCategoryName.includes('Kategori')) {
            const contentData = seoContentDatabase.categories[selectedCategoryName];
            if (contentData) {
                contentTitle = `${contentData.title} Hakkında`;
                contentText = contentData.content;
                contentKeywords = contentData.keywords;
            }
        }
        // Sadece Mevsim
        else if (selectedSeasonName) {
            const contentData = seoContentDatabase.seasons[selectedSeasonName];
            if (contentData) {
                contentTitle = `${contentData.title} Hakkında`;
                contentText = contentData.content;
                contentKeywords = contentData.keywords;
            }
        }

        if (!contentText) return null;

        return (
            <div className="bg-white rounded-lg shadow-sm p-6 md:p-8 my-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 flex items-center">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-lg mr-3">📚</span>
                    {contentTitle}
                </h2>

                <div className="prose max-w-none text-gray-700">
                    {contentText.split('\n\n').map((paragraph: string, index: number) => {
                        if (paragraph.trim().startsWith('**') && paragraph.trim().endsWith('**')) {
                            return (
                                <h3 key={index} className="text-xl font-bold text-gray-800 mt-6 mb-3">
                                    {paragraph.replace(/\*\*/g, '')}
                                </h3>
                            );
                        }
                        else if (paragraph.trim().startsWith('-')) {
                            const items = paragraph.split('\n').filter(item => item.trim().startsWith('-'));
                            return (
                                <ul key={index} className="list-disc pl-6 mb-4 space-y-2">
                                    {items.map((item, i) => {
                                        const text = item.replace(/^-\s*/, '');
                                        const parts = text.split(/(\*\*.*?\*\*)/g);
                                        return (
                                            <li key={i} className="text-gray-700">
                                                {parts.map((part, j) => {
                                                    if (part.startsWith('**') && part.endsWith('**')) {
                                                        return <strong key={j}>{part.replace(/\*\*/g, '')}</strong>;
                                                    }
                                                    return <span key={j}>{part}</span>;
                                                })}
                                            </li>
                                        );
                                    })}
                                </ul>
                            );
                        }
                        else {
                            const parts = paragraph.split(/(\*\*.*?\*\*)/g);
                            return (
                                <p key={index} className="text-gray-700 leading-relaxed mb-4">
                                    {parts.map((part, j) => {
                                        if (part.startsWith('**') && part.endsWith('**')) {
                                            return <strong key={j} className="font-semibold text-gray-900">{part.replace(/\*\*/g, '')}</strong>;
                                        }
                                        return <span key={j}>{part}</span>;
                                    })}
                                </p>
                            );
                        }
                    })}
                </div>

                {contentKeywords && contentKeywords.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-600 mb-3">İlgili Aramalar:</h4>
                        <div className="flex flex-wrap gap-2">
                            {contentKeywords.slice(0, 10).map((keyword, index) => (
                                <Tag key={index} color="orange" className="text-sm">
                                    {keyword}
                                </Tag>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-8 p-6 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg border border-orange-200">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div>
                            <h4 className="text-lg font-bold text-gray-800 mb-2">
                                📞 Lastik Seçiminde Yardıma mı İhtiyacınız Var?
                            </h4>
                            <p className="text-gray-600">
                                Uzman ekibimiz size en uygun lastiği seçmenizde yardımcı olur.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <a
                                href="tel:05441205454"
                                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors text-center whitespace-nowrap"
                            >
                                📞 0544 120 54 54
                            </a>
                            <a
                                href="/iletisim"
                                className="bg-white hover:bg-gray-50 text-gray-800 px-6 py-3 rounded-lg font-semibold border-2 border-gray-300 transition-colors text-center whitespace-nowrap"
                            >
                                💬 İletişim
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const seoContent = generateSEOContent();
    const canonicalUrl = `https://sakaryalastik.com/lastik${location.search}`;

    // Breadcrumb'ı dinamik oluştur
    const breadcrumbItems: any[] = [
        { title: <a href="/">Anasayfa</a> },
        { title: <a href="/lastik">Lastikler</a> },
    ];

    // Kategori varsa ekle
    if (selectedCategoryName && !selectedCategoryName.includes('Kategori')) {
        breadcrumbItems.push({
            title: selectedCategoryName
        });
    }

    // Marka varsa ekle
    if (selectedBrandName) {
        breadcrumbItems.push({
            title: selectedBrandName
        });
    }

    // Mevsim varsa ekle
    if (selectedSeasonName) {
        breadcrumbItems.push({
            title: `${selectedSeasonName} Lastikler`
        });
    }

    return (
        <MainLayout>
            <Helmet>
                <title>{seoContent.title}</title>
                <meta name="description" content={seoContent.description} />
                <meta name="keywords" content={seoContent.keywords} />

                {/* Canonical URL */}
                <link rel="canonical" href={canonicalUrl} />

                {/* Open Graph Meta Tags */}
                <meta property="og:title" content={seoContent.title} />
                <meta property="og:description" content={seoContent.description} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="Sakarya Lastik" />

                {/* Twitter Card Meta Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoContent.title} />
                <meta name="twitter:description" content={seoContent.description} />

                {/* Additional SEO */}
                <meta name="robots" content="index, follow" />
                <meta name="author" content="Sakarya Lastik" />
                <meta name="language" content="Turkish" />

                {/* Schema.org Yapılandırılmış Veri */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "CollectionPage",
                        "name": seoContent.title,
                        "description": seoContent.description,
                        "url": canonicalUrl,
                        "breadcrumb": {
                            "@type": "BreadcrumbList",
                            "itemListElement": breadcrumbItems.map((item, index) => ({
                                "@type": "ListItem",
                                "position": index + 1,
                                "name": typeof item.title === 'string' ? item.title : item.title?.props?.children || 'Anasayfa',
                                "item": index === 0 ? "https://sakaryalastik.com/" :
                                    index === 1 ? "https://sakaryalastik.com/lastik" :
                                        canonicalUrl
                            }))
                        },
                        "mainEntity": {
                            "@type": "ItemList",
                            "numberOfItems": products?.length || 0,
                            "itemListElement": products?.slice(0, 10).map((product: any, index: number) => ({
                                "@type": "Product",
                                "position": index + 1,
                                "name": product.name,
                                "image": product.images?.[0] || product.image,
                                "brand": {
                                    "@type": "Brand",
                                    "name": product.brand
                                },
                                "offers": {
                                    "@type": "Offer",
                                    "price": product.price,
                                    "priceCurrency": "TRY",
                                    "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                                    "url": `https://sakaryalastik.com/product/${product._id}`
                                }
                            }))
                        }
                    })}
                </script>
            </Helmet>

            <div className='flex w-full bg-gray-50 p-3 border rounded-md'>
                <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className='container mx-auto my-5'>
                <Row gutter={[24, 24]}>
                    {/* Mobile Filter Button */}
                    <Col xs={24} lg={0} className="mb-4">
                        <Button
                            type="primary"
                            icon={<FilterOutlined />}
                            onClick={() => setIsFilterVisible(!isFilterVisible)}
                            className="w-full"
                        >
                            Filtreler {getActiveFiltersCount() > 0 && `(${getActiveFiltersCount()})`}
                        </Button>
                    </Col>

                    {/* Filters Sidebar */}
                    <Col xs={isFilterVisible ? 24 : 0} lg={6}>
                        <Card title="Filtreler" className="h-fit sticky top-4">
                            <div className="space-y-4">
                                {/* Clear Filters */}
                                <div className="flex justify-between items-center">
                                    <span className="font-semibold">Aktif Filtreler ({getActiveFiltersCount()})</span>
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<ClearOutlined />}
                                        onClick={clearAllFilters}
                                    >
                                        Temizle
                                    </Button>
                                </div>

                                <Collapse ghost defaultActiveKey={['search', 'category', 'price_range', 'brand', 'tire_type', 'season', 'tire_size']}>
                                    {/* Search */}
                                    <Panel header="Arama" key="search">
                                        <Input.Search
                                            placeholder="Ürün, marka veya ebat ara (örn: 205, Michelin)..."
                                            value={filters.search}
                                            onChange={(e) => handleSearchChange(e.target.value)}
                                            onSearch={(value) => handleFilterChange('search', value)}
                                            allowClear
                                            enterButton
                                            loading={searchLoading}
                                        />
                                        {searchSuggestions.length > 0 && filters.search && (
                                            <div className="mt-2 border rounded-md bg-white shadow-sm">
                                                {searchSuggestions.map((suggestion, index) => (
                                                    <div
                                                        key={index}
                                                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                                                        onClick={() => {
                                                            handleSearchSelect(suggestion.value, suggestion);
                                                        }}
                                                    >
                                                        {suggestion.label}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </Panel>

                                    {/* Categories */}
                                    <Panel header="Kategori" key="category">
                                        <div className="max-h-48 overflow-y-auto space-y-2">
                                            {filterOptions.categories?.map((category: any) => (
                                                <Checkbox
                                                    key={category.name}
                                                    checked={filters.category.includes(category.name)}
                                                    onChange={(e) => {
                                                        const newCategories = e.target.checked
                                                            ? [...filters.category, category.name]
                                                            : filters.category.filter(name => name !== category.name);
                                                        handleFilterChange('category', newCategories);
                                                    }}
                                                >
                                                    <div className="flex justify-between w-full pr-2">
                                                        <span>{category.name}</span>
                                                        <span className="text-gray-700 ml-2">({category.count})</span>
                                                    </div>
                                                </Checkbox>
                                            ))}
                                        </div>
                                    </Panel>

                                    {/* Price Ranges */}
                                    <Panel header="Fiyat Aralığı" key="price_range">
                                        <div className="space-y-2">
                                            {PRICE_RANGES.map((range) => (
                                                <Checkbox
                                                    key={range.value}
                                                    checked={filters.price_ranges.includes(range.value)}
                                                    onChange={(e) => {
                                                        const newPriceRanges = e.target.checked
                                                            ? [...filters.price_ranges, range.value]
                                                            : filters.price_ranges.filter(pr => pr !== range.value);
                                                        handleFilterChange('price_ranges', newPriceRanges);
                                                    }}
                                                >
                                                    <span>{range.label}</span>
                                                </Checkbox>
                                            ))}
                                        </div>
                                    </Panel>

                                    {/* Brands */}
                                    <Panel header="Marka" key="brand">
                                        <div className="max-h-48 overflow-y-auto space-y-2">
                                            {filterOptions.brands?.map((brand: any) => (
                                                <Checkbox
                                                    key={brand.name}
                                                    checked={filters.brand.includes(brand.name)}
                                                    onChange={(e) => {
                                                        const newBrands = e.target.checked
                                                            ? [...filters.brand, brand.name]
                                                            : filters.brand.filter(b => b !== brand.name);
                                                        handleFilterChange('brand', newBrands);
                                                    }}
                                                >
                                                    <div className="flex justify-between w-full pr-2">
                                                        <span>{brand.name}</span>
                                                        <span className="text-gray-700 ml-2">({brand.count})</span>
                                                    </div>
                                                </Checkbox>
                                            ))}
                                        </div>
                                    </Panel>

                                    {/* Tire Type */}
                                    <Panel header="Lastik Tipi" key="tire_type">
                                        <div className="max-h-48 overflow-y-auto space-y-2">
                                            {filterOptions.tireTypes?.map((type: any) => (
                                                <Checkbox
                                                    key={type.name}
                                                    checked={filters.tire_type.includes(type.name)}
                                                    onChange={(e) => {
                                                        const newTypes = e.target.checked
                                                            ? [...filters.tire_type, type.name]
                                                            : filters.tire_type.filter(t => t !== type.name);
                                                        handleFilterChange('tire_type', newTypes);
                                                    }}
                                                >
                                                    <div className="flex justify-between w-full pr-2">
                                                        <span>{type.name}</span>
                                                        <span className="text-gray-700 ml-2">({type.count})</span>
                                                    </div>
                                                </Checkbox>
                                            ))}
                                        </div>
                                    </Panel>

                                    {/* Season */}
                                    <Panel header="Sezon" key="season">
                                        <div className="max-h-48 overflow-y-auto space-y-2">
                                            {filterOptions.seasons?.map((season: any) => (
                                                <Checkbox
                                                    key={season.name}
                                                    checked={filters.season.includes(season.name)}
                                                    onChange={(e) => {
                                                        const newSeasons = e.target.checked
                                                            ? [...filters.season, season.name]
                                                            : filters.season.filter(s => s !== season.name);
                                                        handleFilterChange('season', newSeasons);
                                                    }}
                                                >
                                                    <div className="flex justify-between w-full pr-2">
                                                        <span>{season.name}</span>
                                                        <span className="text-gray-700 ml-2">({season.count})</span>
                                                    </div>
                                                </Checkbox>
                                            ))}
                                        </div>
                                    </Panel>

                                    {/* Tire Size Combo */}
                                    <Panel header="Lastik Ebatı" key="tire_size">
                                        <Select
                                            placeholder="Lastik ebatı seçin (örn: 185/65/R15)"
                                            value={filters.tire_size}
                                            onChange={(value) => handleFilterChange('tire_size', value)}
                                            allowClear
                                            className="w-full"
                                            showSearch
                                            filterOption={(input, option) =>
                                                String(option?.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                                            }
                                        >
                                            {filterOptions.tireSizes?.map((size: string) => (
                                                <Option key={size} value={size}>
                                                    {size}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Panel>

                                    {/* Load Index */}
                                    <Panel header="Yük İndeksi" key="load_index">
                                        <div className="max-h-48 overflow-y-auto space-y-2">
                                            {filterOptions.loadIndexes?.map((index: number) => (
                                                <Checkbox
                                                    key={index}
                                                    checked={filters.load_index.includes(index)}
                                                    onChange={(e) => {
                                                        const newIndexes = e.target.checked
                                                            ? [...filters.load_index, index]
                                                            : filters.load_index.filter(i => i !== index);
                                                        handleFilterChange('load_index', newIndexes);
                                                    }}
                                                >
                                                    {index}
                                                </Checkbox>
                                            ))}
                                        </div>
                                    </Panel>

                                    {/* Speed Index */}
                                    <Panel header="Hız İndeksi" key="speed_index">
                                        <div className="max-h-48 overflow-y-auto space-y-2">
                                            {filterOptions.speedIndexes?.map((index: string) => (
                                                <Checkbox
                                                    key={index}
                                                    checked={filters.speed_index.includes(index)}
                                                    onChange={(e) => {
                                                        const newIndexes = e.target.checked
                                                            ? [...filters.speed_index, index]
                                                            : filters.speed_index.filter(i => i !== index);
                                                        handleFilterChange('speed_index', newIndexes);
                                                    }}
                                                >
                                                    {index}
                                                </Checkbox>
                                            ))}
                                        </div>
                                    </Panel>
                                </Collapse>
                            </div>
                        </Card>
                    </Col>

                    {/* Products Grid */}
                    <Col xs={24} lg={18}>
                        {/* Active Filters Tags */}
                        {getActiveFiltersCount() > 0 && (
                            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                                <div className="flex flex-wrap gap-2">
                                    {filters.search && (
                                        <Tag
                                            closable
                                            onClose={() => handleFilterChange('search', '')}
                                            color="blue"
                                        >
                                            Arama: {filters.search}
                                        </Tag>
                                    )}
                                    {filters.category.map((category: string) => (
                                        <Tag
                                            key={category}
                                            closable
                                            onClose={() => handleFilterChange('category', filters.category.filter(c => c !== category))}
                                            color="cyan"
                                        >
                                            Kategori: {category}
                                        </Tag>
                                    ))}
                                    {filters.brand.map((brand: string) => (
                                        <Tag
                                            key={brand}
                                            closable
                                            onClose={() => handleFilterChange('brand', filters.brand.filter(b => b !== brand))}
                                            color="green"
                                        >
                                            Marka: {brand}
                                        </Tag>
                                    ))}
                                    {filters.season.map((season: string) => (
                                        <Tag
                                            key={season}
                                            closable
                                            onClose={() => handleFilterChange('season', filters.season.filter(s => s !== season))}
                                            color="orange"
                                        >
                                            Sezon: {season}
                                        </Tag>
                                    ))}
                                    {filters.tire_size && (
                                        <Tag
                                            closable
                                            onClose={() => handleFilterChange('tire_size', null)}
                                            color="purple"
                                        >
                                            Ebat: {filters.tire_size}
                                        </Tag>
                                    )}
                                </div>
                            </div>
                        )}                        {/* Products Header */}
                        {/* SEO için H1 başlık - Dinamik */}
                        <div className="container mx-auto my-5">
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                                {seoContent.h1Title}
                            </h1>
                            {products && products.length > 0 && (
                                <p className="text-gray-600">
                                    {/* {products.length} ürün bulundu */}
                                    {filters.price_ranges.length > 0 &&
                                        ` • ${filters.price_ranges.map(range => {
                                            const rangeObj = PRICE_RANGES.find(r => r.value === range);
                                            return rangeObj?.label;
                                        }).join(', ')}`}
                                </p>
                            )}
                        </div>

                        {/* Structured Data for Product List */}
                        {products && products.length > 0 && (
                            <script type="application/ld+json">
                                {JSON.stringify({
                                    "@context": "https://schema.org",
                                    "@type": "ItemList",
                                    "name": seoContent.title,
                                    "description": seoContent.description,
                                    "numberOfItems": products.length,
                                    "itemListElement": products.slice(0, 10).map((product: any, index: number) => ({
                                        "@type": "ListItem",
                                        "position": index + 1,
                                        "item": {
                                            "@type": "Product",
                                            "name": product.name,
                                            "brand": {
                                                "@type": "Brand",
                                                "name": product.brand_name
                                            },
                                            "description": `${product.brand_name} ${product.tire_size} lastik`,
                                            "sku": product.sku || product._id,
                                            "image": product.downloaded_images?.[0] ? `https://sakaryalastik.com/uploads/${product.downloaded_images[0]}` : '',
                                            "offers": {
                                                "@type": "Offer",
                                                "price": product.discounted_price || product.price,
                                                "priceCurrency": "TRY",
                                                "availability": product.stock_amount > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                                                "url": `https://sakaryalastik.com/lastik/${product._id}`
                                            },
                                            "aggregateRating": product.rating ? {
                                                "@type": "AggregateRating",
                                                "ratingValue": product.rating,
                                                "reviewCount": product.review_count || 1
                                            } : undefined
                                        }
                                    }))
                                })}
                            </script>
                        )}

                        {/* Breadcrumb Structured Data */}
                        <script type="application/ld+json">
                            {JSON.stringify({
                                "@context": "https://schema.org",
                                "@type": "BreadcrumbList",
                                "itemListElement": [
                                    {
                                        "@type": "ListItem",
                                        "position": 1,
                                        "name": "Anasayfa",
                                        "item": "https://sakaryalastik.com/"
                                    },
                                    {
                                        "@type": "ListItem",
                                        "position": 2,
                                        "name": selectedCategoryName || "Lastikler",
                                        "item": canonicalUrl
                                    }
                                ]
                            })}
                        </script>

                        {/* Products Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
                            {!products ? (
                                // Loading state - ilk yüklenme
                                <div className="col-span-full flex justify-center py-12">
                                    <Spin size="large" tip="Ürünler yükleniyor..." />
                                </div>
                            ) : products.length > 0 ? (
                                // Ürünler var
                                products.map((product: any) => (
                                    <ProductCard key={product._id} product={product} />
                                ))
                            ) : (
                                // Ürün yok - Şık "Bulunamadı" mesajı
                                <div className="col-span-full flex flex-col items-center justify-center py-16 px-4">
                                    <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-100">
                                        {/* Icon */}
                                        <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-orange-100 to-orange-50 rounded-full flex items-center justify-center">
                                            <svg
                                                className="w-12 h-12 text-orange-500"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                                />
                                            </svg>
                                        </div>

                                        {/* Title */}
                                        <h3 className="text-2xl font-bold text-gray-800 mb-3">
                                            Ürün Bulunamadı
                                        </h3>

                                        {/* Description */}
                                        <p className="text-gray-600 mb-6 leading-relaxed">
                                            {filters.search ? (
                                                <>Aradığınız <span className="font-semibold text-orange-600">"{filters.search}"</span> terimiyle eşleşen ürün bulunamadı.</>
                                            ) : filters.category.length > 0 || filters.brand.length > 0 || filters.season.length > 0 ? (
                                                <>Seçtiğiniz filtrelere uygun ürün bulunmamaktadır. Lütfen farklı filtreler deneyiniz.</>
                                            ) : (
                                                <>Bu kategoride henüz ürün bulunmamaktadır. Yakında yeni ürünler eklenecektir.</>
                                            )}
                                        </p>

                                        {/* Action Buttons */}
                                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                            {getActiveFiltersCount() > 0 && (
                                                <Button
                                                    type="default"
                                                    size="large"
                                                    icon={<ClearOutlined />}
                                                    onClick={clearAllFilters}
                                                    className="shadow-sm hover:shadow-md transition-shadow"
                                                >
                                                    Filtreleri Temizle
                                                </Button>
                                            )}
                                            <Button
                                                type="primary"
                                                size="large"
                                                onClick={() => navigate('/lastik')}
                                                className="bg-gradient-to-r from-orange-500 to-orange-600 border-0 shadow-md hover:shadow-lg transition-shadow"
                                            >
                                                Tüm Ürünlere Dön
                                            </Button>
                                        </div>

                                        {/* Suggestions */}
                                        {(filters.category.length > 0 || filters.brand.length > 0) && (
                                            <div className="mt-8 pt-6 border-t border-gray-200">
                                                <p className="text-sm text-gray-500 mb-3">Popüler kategorilerimize göz atın:</p>
                                                <div className="flex flex-wrap gap-2 justify-center">
                                                    <Tag
                                                        className="cursor-pointer hover:bg-orange-50 transition-colors px-3 py-1"
                                                        onClick={() => navigate('/lastik?category=Otomobil')}
                                                    >
                                                        🚗 Otomobil
                                                    </Tag>
                                                    <Tag
                                                        className="cursor-pointer hover:bg-orange-50 transition-colors px-3 py-1"
                                                        onClick={() => navigate('/lastik?category=SUV')}
                                                    >
                                                        🚙 SUV
                                                    </Tag>
                                                    <Tag
                                                        className="cursor-pointer hover:bg-orange-50 transition-colors px-3 py-1"
                                                        onClick={() => navigate('/lastik?season=Kış')}
                                                    >
                                                        ❄️ Kış Lastikleri
                                                    </Tag>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Pagination */}
                        {products?.length > 0 && totalPages > 1 && (
                            <div className="flex justify-center">
                                <Pagination
                                    current={currentPage}
                                    total={totalPages * filters.limit}
                                    pageSize={filters.limit}
                                    showSizeChanger={false}
                                    showQuickJumper
                                    showTotal={(total, range) =>
                                        `${range[0]}-${range[1]} / ${total} ürün`
                                    }
                                    onChange={handlePageChange}
                                />
                            </div>
                        )}
                    </Col>
                </Row>

                {/* SEO İçerik - Dinamik Kategori/Marka Açıklamaları */}
                {renderSEOContent()}
            </div>
        </MainLayout>
    )
}

export default Products
// ...existing code...

